// @ts-nocheck
import { ChangeEvent, useState, useEffect, useMemo, useRef } from "react";
import { useLocation } from "wouter";
import type React from "react";
import SignatureCanvas from "react-signature-canvas";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { EnrollmentBadges } from "@/components/ui/benefits/EnrollmentBadges";
import { DeductionSummaryContent } from "@/components/ui/benefits/DeductionSummaryContent";
import { isActiveEnrollment, isCurrentlyEffectiveEnrollment, isPendingEnrollment, filterWaivedEnrollments, calculatePremiumForSelection, computePremiumResultForSelection, resolveAvailablePlans, determineCoverageTierFromDependents, coverageStartForEvent, filterPlansByCoverageWindow, clampEffectiveToPlanStart } from "@/lib/enrollmentFilters";
import { CoverageHeaderBanner } from "@/components/benefits/v2/CoverageHeaderBanner";
import { BenefitSection } from "@/components/benefits/v2/BenefitSection";
import { ActiveBenefitRow } from "@/components/benefits/v2/ActiveBenefitRow";
import { PremiumBreakdownPopover } from "@/components/benefits/v2/PremiumBreakdownPopover";
import { WaivedBenefitRow } from "@/components/benefits/v2/WaivedBenefitRow";
import { useTypeSections } from "@/components/benefits/v2/useTypeSections";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Heart,
  Shield,
  ShieldCheck,
  Eye,
  DollarSign,
  Users,
  CheckCircle,
  Info,
  Check,
  X,
  Calculator,
  Clock,
  FileText,
  Calendar,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  Edit,
  Smile,
  Umbrella,
  Loader2,
  AlertCircle,
} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { formatPhoneInputValue } from "@/lib/phone";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { useAuth } from "@/hooks/useAuth";
import { useEnrollmentStatus } from "@/hooks/useEnrollmentStatus";
import { calculateQLEEffectiveDate } from "@shared/utils/qleTypes";
import { resolveEnrollmentEffectiveDate } from "@shared/utils/effectiveDate";
import { useClientPlans } from "@/hooks/useClientPlans";
import { EnrollmentWizard } from "@/components/ui/benefits/EnrollmentWizard";
import { PlanComparisonDialog, type ComparablePlan } from "@/components/benefits/PlanComparisonDialog";
import { useBenefitDetailData } from "@/components/benefits/detail/useBenefitDetailData";
import { CollapsibleBenefitDetails } from "@/components/benefits/detail/CollapsibleBenefitDetails";
import { DependentQuickAddForm, type DependentQuickAddHandle } from "@/components/dependents/DependentQuickAddForm";
import { formatBenefitAmount } from "@shared/utils/enrollmentCalculator";
import { eligibleDependentsForPlan } from "@shared/utils/dependentEligibility";
import { isLifeDisabilityType } from "@shared/schemas/plans";
import OpenEnrollmentConfirmation from "@/components/OpenEnrollmentConfirmation";
import { EnrollmentSigningView } from "@/components/enrollment/EnrollmentSigningView";
import { isHealthOrMedicalPlan } from "@shared/schema";
import { formatCurrency, formatDateDisplay, formatDateSafe, parseDateLocal } from "@/lib/utils";
import { groupEnrollmentsByPlan } from "@/lib/enrollmentGrouping";
import {
  formatPremiumWithPayCycle,
  getPayCycleLabel,
  getPayCycleLabelCapitalized,
  calculatePerPaycheckAmount,
  hasNonMonthlyPayCycle,
} from "@/lib/payrollUtils";
import { computeFsaPreview, isFsaConfigured, fsaNeedsSetup, validateFsaElection as validateFsaElectionInput, fsaEmployeeCost, type FsaPreviewOptions, FSA_NO_PAY_SCHEDULE_EMPLOYEE } from "@/lib/fsaPreview";
import { FsaElectionInput } from "@/components/benefits/v2/FsaElectionInput";
import { buildEnrollmentPayload } from "@/lib/enrollmentPayload";
import { PendingEnrollmentRequestCard } from "@/components/benefits/PendingEnrollmentRequestCard";
import { isEnrollmentRequestTask, isPendingEnrollmentRequestTask } from "@/lib/enrollmentTasks";
import { useFsaConfigs } from "@/hooks/useFsaConfigs";
import { usePaySchedule } from "@/hooks/usePaySchedule";
import { useEnrollmentDraft } from "@/hooks/useEnrollmentDraft";
// DEV-262: shared premium-data loader (rates + contributions incl. buy-up base plans)
import { usePlanPremiumData } from "@/hooks/usePlanPremiumData";
import { useLifeDisabilityConfigs } from "@/hooks/useLifeDisabilityConfigs";
import { planCardPriceState } from "@/lib/planCardPriceState";
import { enrollmentEmployeeMonthly } from "@/lib/planPriceDisplay";
import { Skeleton } from "@/components/ui/skeleton";
import { computeFsaProration, isPayScheduleAnchored } from "@shared/utils/fsaProration";
import { isFsaPlanType, getPlanTypeMeta, requiresBeneficiary, normalizePlanType } from "@shared/schemas/planTypes";
import {
  BeneficiaryAllocation,
  type BeneficiaryAllocationValue,
  defaultBeneficiaryAllocation,
  mirrorPrimaryToLegacy,
  saveBeneficiaries,
} from "@/components/benefits/BeneficiaryAllocation";
import { SectionHeader, DependentsBody, WaiveReasonPicker } from "@/components/benefits/v2/modifySteps";
import { WAIVE_REASONS, classifyDepReason, flattenDepReasons } from "@shared/utils/waiveReasons";

// Form schema for qualifying life events
const qualifyingEventSchema = z.object({
  eventType: z.string().min(1, "Please select an event type"),
  eventDate: z.string().min(1, "Event date is required"),
  description: z
    .string()
    .min(10, "Please provide at least 10 characters describing the event"),
  documentation: z.string().optional(),
});

// Form schema for beneficiaries
const beneficiarySchema = z.object({
  id: z.number().optional(),
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  relationship: z.string().min(1, "Please select a relationship"),
  dateOfBirth: z.string().min(1, "Date of birth is required"),
  percentage: z
    .number()
    .min(1, "Percentage must be at least 1%")
    .max(100, "Percentage cannot exceed 100%"),
  ssn: z.string().optional(),
  address: z.string().optional(),
  phone: z.string().optional(),
});

// DEV-262: removed BenefitPlan/BenefitCategory interfaces — they modeled the
// legacy layout's mock data, including flat premiumEmployee/premiumSpouse/
// premiumChild/premiumFamily fields that bypassed real rate tables. Live code
// prices through calculatePremiumForSelection with usePlanPremiumData maps.

// Benefit types the employee enrollment flow can step through, in display order.
// Mirrors the canonical TYPE_RANK / SECTION_ORDER (planTypes.ts). Life and AD&D
// are distinct types (employee can elect BOTH); std/ltd are the disability
// leaves. Legacy "medical" folds into "health" via normalizePlanType.
const ENROLLABLE_BENEFIT_TYPE_ORDER = [
  "health",
  "dental",
  "vision",
  "life",
  "add",
  "std",
  "ltd",
  "fsa_health",
  "fsa_dependent_care",
];

export default function Benefits() {
  const [, navigate] = useLocation();
  // DEV-262: removed legacy-layout states here (selectedPlans, isCalculating)
  const [showQualifyingEventForm, setShowQualifyingEventForm] = useState(false);
  // Inline "add a dependent" draft in the QLE modal — created automatically on submit.
  const qleDependentFormRef = useRef<DependentQuickAddHandle>(null);
  // DEV-262: removed legacy-layout states here (collapsedCategories,
  // collapsedCurrentBenefits, expandedPlans — only the deleted legacy JSX read them)
  const [comparePlans, setComparePlans] = useState<Record<string, number[]>>(
    {}
  );
  const [showModifyBenefitsModal, setShowModifyBenefitsModal] = useState(false);
  const [modifyStep, setModifyStep] = useState(0); // 0: medical, 1: dental, 2: vision, etc.
  // When non-null, opens the Plan Comparison dialog for plans selected for this benefit type.
  const [compareDialogType, setCompareDialogType] = useState<string | null>(null);
  const [showSummaryPage, setShowSummaryPage] = useState(false);
  const [showSignaturePage, setShowSignaturePage] = useState(false);
  const [selectedModifyPlans, setSelectedModifyPlans] = useState<{
    [key: string]: { planId: number; dependentIds: number[]; annualElection?: string; beneficiaries?: BeneficiaryAllocationValue };
  }>({});
  // Per-type, per-dependent waive reason — two parallel maps mirror
  // ModifyTypeModal's DependentsBody API exactly. `depReasonByType` holds the
  // dropdown selection (slug or "other"); `depOtherTextByType` holds the
  // freeform text for the "other" branch. Flattened to the legacy
  // `Record<depId, string>` wire format at save time via flattenDepReasons().
  const [depReasonByType, setDepReasonByType] = useState<Record<string, Record<number, string>>>({});
  const [depOtherTextByType, setDepOtherTextByType] = useState<Record<string, Record<number, string>>>({});
  const [waivedCoverageByType, setWaivedCoverageByType] = useState<
    Record<string, boolean>
  >({});
  const [waiverReasonByType, setWaiverReasonByType] = useState<
    Record<string, string>
  >({});
  // Freeform "Other" text paired with waiverReasonByType — only meaningful when
  // the picked reason slug is "other". Mirrors the modal's `waiveOtherText`.
  const [waiverDescriptionByType, setWaiverDescriptionByType] = useState<
    Record<string, string>
  >({});
  const [pendingEnrollments, setPendingEnrollments] = useState<any[]>([]);
  const [unlockedModifySteps, setUnlockedModifySteps] = useState<Set<number>>(new Set());
  const [showPendingModificationsModal, setShowPendingModificationsModal] =
    useState(false);
  const [showComparison, setShowComparison] = useState<Record<string, boolean>>(
    {}
  );
  // DEV-262: removed legacy-layout states here (savedBenefits, showSignatureModal,
  // signatureName, signatureData — the legacy signature modal is gone; the live
  // signing flow keeps its own local state)
  const [isSaving, setIsSaving] = useState(false);
  const [showFormReview, setShowFormReview] = useState(false);
  const [formReviewData, setFormReviewData] = useState<{
    signatureName: string;
    signatureData: string;
    signedContent?: any;
  } | null>(null);
  const [showBeneficiariesModal, setShowBeneficiariesModal] = useState(false);
  const [currentPlanId, setCurrentPlanId] = useState<number | null>(null);
  const [qleSubmitting, setQleSubmitting] = useState(false);
  const [qleJustSubmitted, setQleJustSubmitted] = useState(false);
  const [qleFile, setQleFile] = useState<File | null>(null);

  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const {
    enrollmentState, canMakeChanges,
    isOpenEnrollmentActive, oeWindow, oeProgressRow,
    hasCompletedOpenEnrollment, oeProgressKey,
    newHireWindow,
    qualifyingEvents,
    qleEffectiveDate,
    qleEventDate,
  } = useEnrollmentStatus(user?.clientId, user?.employeeId);

  const withinOpenEnrollment = isOpenEnrollmentActive;

  // --- Enrollment autosave / resume (DEV-213) -----------------------------
  // Persist in-progress elections (and, via the signing view, signing
  // progress) server-side so the member — or a broker impersonating them —
  // can leave and resume without redoing the flow. Keyed by the enrollment
  // period so OE / QLE / new-hire drafts never collide.
  const draftPeriodKey = useMemo(() => {
    if (enrollmentState === "open_enrollment")
      return oeProgressKey ? `oe:${String(oeProgressKey).split("T")[0]}` : "oe";
    if (enrollmentState === "qualifying_event")
      return qleEventDate ? `qle:${String(qleEventDate).split("T")[0]}` : "qle";
    if (enrollmentState === "new_hire") return "new_hire";
    return null;
  }, [enrollmentState, oeProgressKey, qleEventDate]);

  const {
    draft: enrollmentDraft,
    isLoaded: draftLoaded,
    saveDraft: saveEnrollmentDraft,
    clearDraft: clearEnrollmentDraft,
  } = useEnrollmentDraft(user?.employeeId, draftPeriodKey);

  // Elections snapshot for autosave.
  const draftWizardState = useMemo(
    () => ({
      modifyStep,
      selectedModifyPlans,
      waivedCoverageByType,
      waiverReasonByType,
      waiverDescriptionByType,
      depReasonByType,
      depOtherTextByType,
    }),
    [
      modifyStep,
      selectedModifyPlans,
      waivedCoverageByType,
      waiverReasonByType,
      waiverDescriptionByType,
      depReasonByType,
      depOtherTextByType,
    ],
  );

  const startEnrollmentOver = () => {
    clearEnrollmentDraft();
    setSelectedModifyPlans({});
    setWaivedCoverageByType({});
    setWaiverReasonByType({});
    setWaiverDescriptionByType({});
    setDepReasonByType({});
    setDepOtherTextByType({});
    setModifyStep(0);
  };

  // Restore a saved draft once, when the modify flow opens. `draftRestoreDone`
  // gates autosave so the empty pre-restore state can't clobber a saved draft.
  const [draftRestoreDone, setDraftRestoreDone] = useState(false);
  useEffect(() => {
    if (!showModifyBenefitsModal) {
      if (draftRestoreDone) setDraftRestoreDone(false);
      return;
    }
    if (draftRestoreDone || !draftLoaded) return;

    const ws = enrollmentDraft?.wizardState as any;
    const hasSelections =
      ws?.selectedModifyPlans && Object.keys(ws.selectedModifyPlans).length > 0;
    const hasWaivers =
      ws?.waivedCoverageByType && Object.keys(ws.waivedCoverageByType).length > 0;
    // Only restore into an untouched wizard so live edits are never clobbered.
    const wizardUntouched = Object.keys(selectedModifyPlans).length === 0;

    if ((hasSelections || hasWaivers) && wizardUntouched) {
      if (ws.selectedModifyPlans) setSelectedModifyPlans(ws.selectedModifyPlans);
      if (ws.waivedCoverageByType) setWaivedCoverageByType(ws.waivedCoverageByType);
      if (ws.waiverReasonByType) setWaiverReasonByType(ws.waiverReasonByType);
      if (ws.waiverDescriptionByType)
        setWaiverDescriptionByType(ws.waiverDescriptionByType);
      if (ws.depReasonByType) setDepReasonByType(ws.depReasonByType);
      if (ws.depOtherTextByType) setDepOtherTextByType(ws.depOtherTextByType);
      if (typeof ws.modifyStep === "number") setModifyStep(ws.modifyStep);
      toast({
        title: "Resumed your saved progress",
        description:
          "We restored your in-progress elections. Keep going or start over.",
        action: (
          <ToastAction altText="Start over" onClick={startEnrollmentOver}>
            Start over
          </ToastAction>
        ),
      });
    }
    setDraftRestoreDone(true);
  }, [showModifyBenefitsModal, draftLoaded, draftRestoreDone, enrollmentDraft]);

  // Autosave elections while the modify flow is open (debounced in the hook).
  // Skips truly-empty state so we don't create noise rows on a fresh open.
  useEffect(() => {
    if (!showModifyBenefitsModal || !draftRestoreDone || !draftPeriodKey) return;
    const hasContent =
      Object.keys(selectedModifyPlans).length > 0 ||
      Object.keys(waivedCoverageByType).length > 0;
    if (!hasContent) return;
    saveEnrollmentDraft({
      wizardState: draftWizardState,
      enrollmentSource:
        enrollmentState === "new_hire"
          ? "new_hire"
          : enrollmentState === "qualifying_event"
            ? "qualifying_event"
            : "open_enrollment",
    });
  }, [
    showModifyBenefitsModal,
    draftRestoreDone,
    draftPeriodKey,
    draftWizardState,
    saveEnrollmentDraft,
    enrollmentState,
  ]);

  // Employee confirmation: mark OE progress complete
  const confirmOeSelection = useMutation({
    mutationFn: async () => {
      const clientId = user?.clientId;
      const employeeId = user?.employeeId;
      const startDate = oeProgressKey || oeWindow?.startDate;
      if (!clientId || !employeeId || !startDate) return;
      const res = await fetch(
        `/api/clients/${clientId}/open-enrollment/progress`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            employeeId,
            startDate,
            status: "complete",
          }),
        }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.message || "Failed to confirm selection");
      }
      return res.json();
    },
    onSuccess: () => {
      toast({
        title: "Enrollment confirmed",
        description: "Your open enrollment selection has been confirmed.",
      });
      queryClient.invalidateQueries({
        queryKey: [
          "/api/clients",
          user?.clientId,
          "open-enrollment",
          "progress",
          oeProgressKey,
          user?.employeeId,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/employees", user?.employeeId, "enrollments-oe-fallback"],
      });
    },
    onError: (e: any) => {
      toast({
        title: "Confirmation failed",
        description: e?.message || "Please try again.",
        variant: "destructive",
      });
    },
  });

  // Parse selected plan IDs from OE window for reuse and debugging
  const oeSelectedPlanIds: number[] = useMemo(() => {
    const rawSelected = (oeWindow as any)?.selectedPlanIds as any;
    if (Array.isArray(rawSelected)) {
      return (rawSelected as any[])
        .map((id) => parseInt(String(id)))
        .filter((n) => Number.isFinite(n));
    }
    if (typeof rawSelected === "string" && rawSelected.trim().length > 0) {
      try {
        const parsed = JSON.parse(rawSelected);
        if (Array.isArray(parsed)) {
          return (parsed as any[])
            .map((id) => parseInt(String(id)))
            .filter((n) => Number.isFinite(n));
        }
      } catch {
        const m = rawSelected.match(/^\{(.+)\}$/);
        if (m && m[1]) {
          return m[1]
            .split(",")
            .map((s) => parseInt(s.trim()))
            .filter((n) => Number.isFinite(n));
        }
      }
    }
    return [] as number[];
  }, [oeWindow]);

  // Plans for this client - use shared hook
  const { plans: employeePlansData, allPlans: employeeAllPlans, isLoading: plansLoading } = useClientPlans(user?.clientId);

  // Filter to plans eligible today and selected in OE
  // Uses availableStartDate (when OE can begin showing this plan) and
  // availableEndDate (late enrollment cutoff) with effectiveDate/endDate as fallbacks
  const eligibleOePlans = (() => {
    if (!oeWindow) return [] as any[];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selectedIds: number[] = oeSelectedPlanIds;
    return (employeePlansData || []).filter((p: any) => {
      // Empty selection offers NOTHING — brokers must pick the offered plans
      // in Client → Benefits → Open Enrollment (no all-plans fallback).
      if (!selectedIds.includes(p.id)) return false;
      // When the plan becomes available for enrollment (availableStartDate, fallback to effectiveDate)
      const start = p?.availableStartDate ? parseDateLocal(p.availableStartDate)
        : p?.effectiveDate ? parseDateLocal(p.effectiveDate) : null;
      // When enrollment closes (availableEndDate, fallback to endDate)
      const cutoff = p?.availableEndDate ? parseDateLocal(p.availableEndDate)
        : p?.endDate ? parseDateLocal(p.endDate) : null;
      const canEnroll = !start || start <= today;
      const notPastCutoff = !cutoff || cutoff >= today;
      return canEnroll && notPastCutoff && (p?.isActive ?? true) !== false;
    });
  })();

  // Broker-selected plans (by OE selectedPlanIds only, no date filtering)
  // Used for new hire enrollment when OE window exists with selected plans
  const brokerFilteredPlans = (() => {
    if (oeSelectedPlanIds.length === 0) return [] as any[];
    return (employeePlansData || []).filter((p: any) => {
      if (!oeSelectedPlanIds.includes(p.id)) return false;
      return (p?.isActive ?? true) !== false;
    });
  })();

  // Fallback: all active client plans (used for new hire enrollment when no OE window is configured)
  const activeClientPlans = useMemo(() => {
    return (employeePlansData || []).filter((p: any) => {
      const isActive = (p?.isActive ?? true) !== false;
      const today = new Date();
      const endDate = p?.endDate ? parseDateLocal(p.endDate) : null;
      const notExpired = !endDate || endDate > today;
      return isActive && notExpired;
    });
  }, [employeePlansData]);

  // Build debug info for all client plans irrespective of OE filters
  const allPlansDebug: any[] = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const ref = oeWindow ? today : null;
    const selectedIds = oeSelectedPlanIds;
    return (employeePlansData || []).map((p: any) => {
      const eff = p?.effectiveDate ? parseDateLocal(p.effectiveDate) : null;
      const end = p?.endDate ? parseDateLocal(p.endDate) : null;
      const startsOk = !ref || !eff || eff <= ref;
      const notEnded = !ref || !end || end >= ref;
      const isActive = (p?.isActive ?? true) !== false;
      const inSelected = selectedIds.includes(p.id);
      const wouldShowInEligible =
        Boolean(ref) && inSelected && startsOk && notEnded && isActive;
      return {
        id: p.id,
        name: p.name,
        type: p.type,
        clientPlanId: p.clientPlanId,
        effectiveDate: p.effectiveDate || null,
        endDate: p.endDate || null,
        isActive,
        inSelected,
        startsOk,
        notEnded,
        wouldShowInEligible,
      };
    });
  }, [employeePlansData, oeWindow, oeSelectedPlanIds]);

  // Wizard state for OE self-enrollment
  const [wizardPlan, setWizardPlan] = useState<any | null>(null);

  // Current employee details
  const { data: employeeRow, isLoading: employeeRowLoading } = useQuery<any>({
    queryKey: ["/api/employees", user?.employeeId],
    enabled: Boolean(user?.employeeId),
    queryFn: async () => {
      const res = await fetch(`/api/employees/${user?.employeeId}`);
      if (!res.ok) return null;
      return res.json();
    },
  });

  // ADR (ONB-7): hard redirect when dateOfBirth is missing.
  // Premium calc requires DOB; rendering /benefits without it produces silent
  // $0 premiums and later enrollment rejection. Strict guard chosen over a
  // banner because a banner can be dismissed/ignored. Reuses employeeRow
  // above — no extra round-trip.
  const dobMissing = !!employeeRow && !employeeRow.dateOfBirth;
  useEffect(() => {
    if (dobMissing) {
      navigate("/onboarding");
    }
  }, [dobMissing, navigate]);

  // Fetch eligibility data for per-plan effective dates
  const { data: eligibilityData } = useQuery<any>({
    queryKey: ["employee-eligibility", employeeRow?.id],
    queryFn: async () => {
      const res = await fetch(`/api/employees/${employeeRow.id}/eligibility`);
      if (!res.ok) return null;
      return res.json();
    },
    enabled: Boolean(employeeRow?.id),
  });

  const eligibilityByPlanId = new Map(
    (eligibilityData?.plans || []).map((p: any) => [p.planId, p])
  );

  // Resolve the plan source list based on enrollment state.
  // For QLE / new hire: all active client plans. For OE: OE-filtered chain.
  const resolvedPlanSource = useMemo(() => {
    const base = resolveAvailablePlans(enrollmentState, eligibleOePlans as any[], brokerFilteredPlans as any[], activeClientPlans as any[], Boolean(oeWindow));

    // QLE / new hire: restrict to the plan YEAR the employee is actually entering.
    // Coverage starts the first of the month after the event (QLE) or hire date
    // (new hire); only plans whose [effectiveDate, endDate] window contains that
    // date should be offered. Prevents showing both the current plan and the
    // upcoming renewal year. Per-type: a type with no in-window plan is dropped.
    if (enrollmentState === "qualifying_event" || enrollmentState === "new_hire") {
      const coverageStart =
        enrollmentState === "qualifying_event"
          ? coverageStartForEvent(qleEventDate, "first_of_month")
          : coverageStartForEvent(
              newHireWindow?.hireDate ? parseDateLocal(newHireWindow.hireDate) : null,
              "first_of_following_month",
            );
      if (coverageStart) {
        const filtered = filterPlansByCoverageWindow(base, coverageStart);
        // Safety net: if windows are misconfigured and everything is filtered out,
        // fall back to the unfiltered list rather than show no plans at all.
        if (filtered.length > 0) return filtered;
      }
    }
    return base;
  }, [enrollmentState, eligibleOePlans, brokerFilteredPlans, activeClientPlans, qleEventDate, newHireWindow, oeWindow]);

  // FSA plans configure via fsaPlanConfig (annual election limits) — shared hook
  // gives a planId → config map for cards / per-paycheck preview / readiness.
  const { fsaConfigByPlanId } = useFsaConfigs();

  // Determine available benefit types based on resolved plan source
  const availableBenefitTypes = useMemo(() => {
    // An FSA type only becomes a step if at least one of its plans is set up
    // (annual election max > 0). Otherwise the plan cards filter would hide
    // every FSA plan and we'd render an empty "Choose FSA plan" step.
    const uniqueTypes = new Set(
      resolvedPlanSource
        .filter((p: any) => !fsaNeedsSetup(p.type, fsaConfigByPlanId.get(p.id)))
        .map((p: any) => normalizePlanType(p.type))
    );
    // Only include types that are actually offered, in canonical order.
    return ENROLLABLE_BENEFIT_TYPE_ORDER.filter((type) => uniqueTypes.has(type));
  }, [resolvedPlanSource, fsaConfigByPlanId]);

  // Pay schedule for FSA proration (annual election ÷ exact remaining paychecks).
  // FSA enrollment requires a schedule that yields an EXACT deduction (no estimate).
  const { resolvePaySchedule, isLoading: payScheduleLoading } = usePaySchedule((employeeRow as any)?.clientId);
  const fsaPaySchedule = resolvePaySchedule((employeeRow as any)?.payGroupId);
  const payScheduleReady = isPayScheduleAnchored(fsaPaySchedule);

  // Thin glue: resolve this plan's coverage-start + plan-year end, then defer to
  // the shared fsaEmployeeCost (client/src/lib/fsaPreview). Keeps the FSA cost
  // math out of this file so the wizard / shared card use the same source.
  const fsaCostFor = (plan: any, annualElectionStr: string | undefined) =>
    fsaEmployeeCost({
      annualElection: annualElectionStr,
      fsaConfig: fsaConfigByPlanId.get(plan.id) || null,
      payCycle: employeeRow?.payCycle,
      schedule: fsaPaySchedule,
      effectiveDate:
        enrollmentState === "qualifying_event" && qleEventDate
          ? qleEventDate instanceof Date
            ? qleEventDate.toISOString()
            : String(qleEventDate)
          : eligibilityByPlanId.get(plan.id)?.effectiveDate || null,
      planYearEnd: (plan as any).endDate || null,
    });

  // Submit OE enrollment for selected plan via standard enrollment API
  const submitOeEnrollment = useMutation({
    mutationFn: async (payload: any) => {
      if (!employeeRow?.id) throw new Error("Missing employee");
      const res = await fetch(`/api/employees/${employeeRow.id}/enrollments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.message || "Failed to submit enrollment");
      }
      return res.json();
    },
    onSuccess: () => {
      toast({
        title: "Enrollment submitted",
        description: "Your open enrollment selection has been saved.",
      });
      setWizardPlan(null);
      queryClient.invalidateQueries({ queryKey: ["/api/enrollments"] });
    },
    onError: (e: any) => {
      toast({
        title: "Submission failed",
        description: e?.message || "Please try again.",
        variant: "destructive",
      });
    },
  });

  // Form for qualifying life events
  const form = useForm<z.infer<typeof qualifyingEventSchema>>({
    resolver: zodResolver(qualifyingEventSchema),
    defaultValues: {
      eventType: "",
      eventDate: "",
      description: "",
      documentation: "",
    },
  });

  // Marriage / birth QLE allow adding the new spouse / child inline
  const qleEventType = form.watch("eventType");
  const qleAllowsDependents =
    qleEventType === "marriage" || qleEventType === "birth";

  // Fetch dependents to determine coverage options (scoped to current employee)
  const { data: dependents = [], isLoading: dependentsLoading } = useQuery({
    queryKey: ["/api/employees", user?.employeeId, "dependents"],
    enabled: Boolean(user?.employeeId),
    queryFn: async () => {
      const res = await fetch(`/api/employees/${user?.employeeId}/dependents`);
      if (!res.ok) return [] as any[];
      return res.json();
    },
  });

  // Employer toggle: may this employee see the premium-cost breakdown popover?
  // Defaults to visible if the setting hasn't been configured.
  const { data: benefitsDisplaySettings } = useQuery<{ showPremiumBreakdownToEmployees?: boolean }>({
    queryKey: ["/api/benefits/display-settings"],
    queryFn: async () => {
      const res = await fetch(`/api/benefits/display-settings`);
      if (!res.ok) return { showPremiumBreakdownToEmployees: true };
      return res.json();
    },
  });
  const allowBreakdown = benefitsDisplaySettings?.showPremiumBreakdownToEmployees !== false;

  // Fetch all plan contributions once (shared across currentEnrollments, contribsByPlanId, selectedPlanContribs)
  const { data: allPlanContributions = [], isSuccess: contribsLoaded } = useQuery<any[]>({
    queryKey: ["/api/plan-contributions"],
    queryFn: async () => {
      const res = await fetch(`/api/plan-contributions`);
      if (!res.ok) return [] as any[];
      return res.json();
    },
  });

  // Fetch current benefit enrollments from DB and enrich with plan details and computed premiums
  const { data: currentEnrollments = [], isLoading: enrollmentsLoading } = useQuery({
    queryKey: [
      "/api/employees",
      user?.employeeId,
      "enrollments-enriched",
      user?.clientId,
      (dependents as any[])?.map((d: any) => d.id).join(",") || "",
      allPlanContributions?.length || 0,
    ],
    enabled: Boolean(user?.employeeId && user?.clientId && employeeRow && Array.isArray(dependents) && contribsLoaded),
    queryFn: async () => {
      // Consume SERVER-computed premiums instead of recomputing client-side.
      // withComputedPremiums resolves each enrollment's snapshotted eligibility
      // -group contribution rule set and buy-up base-plan rates — logic the
      // client can't reproduce from plan/rate data (it filtered contributions by
      // planId only, so multi-group clients got the wrong group's employer %).
      // The /benefits endpoint returns rows enriched with employeePremium/
      // employerPremium/totalPremium + a .premium object, and nests .plan.
      const res = await fetch(`/api/employees/${user?.employeeId}/benefits`);
      if (!res.ok) return [] as any[];
      const rows = (await res.json()) as any[];

      // Group rows by planId using shared utility
      const byPlan = groupEnrollmentsByPlan(rows || []);

      const results: any[] = [];
      for (const [planId, group] of byPlan.entries()) {
        // If no active base but there are waived rows, use the latest waived row as base
        // This prevents ghost entries with undefined fields when the only enrollment is waived
        let base = group.base;
        if (!base && group.waivedRows.length > 0) {
          base = group.waivedRows[group.waivedRows.length - 1];
        }
        if (!base) continue;
        const depIds = group.dependentIds;

        // Use nested plan data from enrollment response, fallback to lookup
        let plan = (base as any)?.plan;
        if (!plan) {
          // Fallback to separate lookup if plan not nested in enrollment
          plan = (employeeAllPlans as any[]).find((p: any) => p.id === planId);
          if (!plan) {
            console.error(`[Employee Portal] Plan ${planId} not found - SKIPPING ENROLLMENT`, {
              planId,
              baseEnrollmentId: base?.id,
              employeeAllPlansCount: employeeAllPlans?.length || 0,
              employeeAllPlansIds: employeeAllPlans?.map((p: any) => p.id)
            });
            continue;
          }
        }

        const premium = (base as any)?.premium || {};
        results.push({
          id: (base as any)?.id,
          planId,
          planName: (plan as any)?.name,
          planType: (plan as any)?.type,
          planCarrier: (plan as any)?.carrier || null,
          coverageLevel: (base as any)?.coverageTier,
          monthlyPremium: Number((base as any)?.employeePremium ?? premium.employeePremium ?? 0),
          employerPremium: Number((base as any)?.employerPremium ?? premium.employerPremium ?? 0),
          totalPremium: Number((base as any)?.totalPremium ?? premium.totalMonthlyRate ?? 0),
          contributionResolved: premium.contributionResolved || null,
          benefitAmount: premium.benefitAmount ?? null,
          benefitAmountPeriod: premium.benefitAmountPeriod ?? null,
          hasLdConfig: premium.benefitAmount != null,
          effectiveDate: (base as any)?.effectiveDate,
          status: (base as any)?.status,
          endDate: (base as any)?.endDate,
          waivedCoverage: Boolean((base as any)?.waivedCoverage),
          waiverReason: (base as any)?.waiverReason || null,
          dependentWaiverReasons: (base as any)?.dependentWaiverReasons || null,
          enrolledDependents: depIds,
          enrollmentSource: (base as any)?.enrollmentSource || null,
        });

        // Also include any additional waived rows so they show in the Waived Benefits section
        for (const wr of group.waivedRows || []) {
          if ((base as any)?.id && (wr as any)?.id === (base as any)?.id) {
            continue;
          }
          results.push({
            id: (wr as any)?.id,
            planId,
            planName: (plan as any)?.name,
            planType: (plan as any)?.type,
            coverageLevel: (wr as any)?.coverageTier || "employee_only",
            monthlyPremium: Number((wr as any)?.employeePremium ?? 0),
            employerPremium: Number((wr as any)?.employerPremium ?? 0),
            totalPremium: Number((wr as any)?.totalPremium ?? 0),
            effectiveDate: (wr as any)?.effectiveDate,
            status: (wr as any)?.status ?? "waived",
            endDate: (wr as any)?.endDate,
            waivedCoverage: true,
            waiverReason: (wr as any)?.waiverReason || null,
            enrolledDependents: [],
            enrollmentSource: (wr as any)?.enrollmentSource || null,
          });
        }
      }

      return results;
    },
  });

  const isInitialLoading = plansLoading || employeeRowLoading || dependentsLoading || enrollmentsLoading;

  // ── v2 read-only "Current Benefits" derivation ─────────────────────────────
  // Lifted to the component top level so hook order is stable across renders
  // (the JSX block that consumes these returns null while data loads).
  const v2CurrentBenefits = useMemo(() => {
    const toBrokerShape = (e: any) => ({
      ...e,
      id: e.id ?? `${e.planType}-${e.planId}`,
      plan: {
        id: e.planId,
        name: e.planName,
        type: e.planType,
        carrier: e.planCarrier,
      },
      coverageTier: e.coverageLevel,
      employeePremium: e.monthlyPremium,
      employerPremium: e.employerPremium,
      totalPremium: e.totalPremium,
      dependentId: null,
    });

    const activeBenefits = (currentEnrollments as any[])
      .filter(isCurrentlyEffectiveEnrollment)
      .map(toBrokerShape);
    const pendingBenefits = (currentEnrollments as any[])
      .filter((e: any) => {
        if (e.waivedCoverage) return false;
        const status = String(e.status || "").toLowerCase();
        if (status === "terminated") return false;
        return status === "pending" || isPendingEnrollment(e);
      })
      .map(toBrokerShape);
    const waivedBenefits = filterWaivedEnrollments(currentEnrollments as any[]).map(toBrokerShape);

    // employeeBenefits feeds ActiveBenefitRow's coveredDependents lookup;
    // include synthesized per-dependent rows so their names render.
    const employeeBenefitsForRows: any[] = [];
    for (const r of (currentEnrollments as any[]).filter((e: any) => !e.waivedCoverage)) {
      const base = toBrokerShape(r);
      employeeBenefitsForRows.push(base);
      if (Array.isArray(r.enrolledDependents)) {
        for (const depId of r.enrolledDependents as number[]) {
          employeeBenefitsForRows.push({
            id: `${r.planId}-dep-${depId}`,
            planId: r.planId,
            dependentId: depId,
            status: "active",
            effectiveDate: r.effectiveDate,
            plan: base.plan,
          });
        }
      }
    }

    const totalMonthly = activeBenefits.reduce(
      (sum: number, e: any) => sum + (Number(e.employeePremium) || 0),
      0,
    );

    return { activeBenefits, pendingBenefits, waivedBenefits, employeeBenefitsForRows, totalMonthly };
  }, [currentEnrollments]);

  const v2TypeSections = useTypeSections({
    activeBenefits: v2CurrentBenefits.activeBenefits,
    pendingBenefits: v2CurrentBenefits.pendingBenefits,
    waivedBenefits: v2CurrentBenefits.waivedBenefits,
    clientPlans: [],
  });

  // Note: Using centralized isActiveEnrollment from enrollmentFilters

  // Plans visible for the current modify step (by type)
  // Filter out plans that employee is currently enrolled in and that expire at/before OE effective date
  const visiblePlans = useMemo(() => {
    if (availableBenefitTypes.length === 0) return [];
    const currentType = availableBenefitTypes[modifyStep] || availableBenefitTypes[0];
    const source = (resolvedPlanSource || []) as any[];

    // Check if we're within OE window
    const isWithinOE = Boolean(
      oeWindow?.startDate &&
      oeWindow?.endDate &&
      (() => {
        const now = new Date();
        const start = parseDateLocal(oeWindow.startDate);
        const end = parseDateLocal(oeWindow.endDate);
        if (!start || !end) return false;
        return start <= now && now <= end;
      })()
    );
    
    // Get currently enrolled plan IDs that expire at/before the plan's renewal date
    const expiringEnrolledPlanIds = new Set<number>();

    if (isWithinOE) {
      (currentEnrollments as any[]).forEach((e: any) => {
        if (e?.planId && isActiveEnrollment(e)) {
          // Use the plan's own renewal date as the reference for expiry
          const matchingPlan = source.find((p: any) => p.id === e.planId);
          const planRenewalDate = matchingPlan?.renewalDate ? parseDateLocal(matchingPlan.renewalDate) : null;
          if (!planRenewalDate) return;
          const enrollmentEndDate = e?.endDate ? parseDateLocal(e.endDate) : null;
          // If enrollment ends at or before renewal date, it's expiring
          if (enrollmentEndDate && enrollmentEndDate <= planRenewalDate) {
            expiringEnrolledPlanIds.add(e.planId);
          }
        }
      });
    }
    
    return source.filter((p: any) => {
      // Filter by type (canonical — folds legacy "medical" into "health")
      if (normalizePlanType(p.type) !== currentType) return false;
      // During OE: exclude plans that employee is currently enrolled in and that expire at/before the plan's renewal date
      if (isWithinOE && expiringEnrolledPlanIds.has(p.id)) {
        return false;
      }
      return true;
    });
  }, [resolvedPlanSource, modifyStep, availableBenefitTypes, currentEnrollments, oeWindow]);

  // Check if current step's coverage is already in effect (locked in modify mode).
  // A step locks ONLY when the coverage that would change is already in force
  // and there's no not-yet-effective election to modify — mid-year changes to
  // in-force coverage require a QLE. Elections that haven't taken effect yet
  // are ALWAYS modifiable, and during an active OE window nothing locks (the
  // employee is electing NEXT plan-year coverage; current coverage being in
  // force is expected, not a reason to lock).
  //
  // NOTE: this deliberately does NOT compare election dates against
  // oeProgressKey — that key is the OE *window start* (when enrollment opens),
  // while elections are written at the *coverage start* (e.g. window opens
  // 7/1, coverage starts 8/1), so an exact-date match can never be relied on.
  const isLockedStep = useMemo(() => {
    if (!hasCompletedOpenEnrollment) return false;
    const currentType = availableBenefitTypes[modifyStep] || "";
    if (!currentType) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // A future-effective election for this type (any source) is not in place
    // yet — always modifiable.
    const hasFutureElection = (currentEnrollments as any[])?.some((e: any) => {
      if (normalizePlanType(e.planType) !== currentType) return false;
      if (e.waivedCoverage) return false;
      const status = String(e.status || "").toLowerCase();
      if (status !== "active" && status !== "pending") return false;
      if (!e.effectiveDate) return false;
      return new Date(e.effectiveDate) > today;
    });
    if (hasFutureElection) return false;

    // During an active OE window every step elects next plan-year coverage.
    if (isOpenEnrollmentActive) return false;

    const activeEnrollment = (currentEnrollments as any[])?.find(
      (e: any) => normalizePlanType(e.planType) === currentType && isActiveEnrollment(e)
    );
    if (!activeEnrollment?.effectiveDate) return false;
    const effDate = new Date(activeEnrollment.effectiveDate);
    return effDate <= today;
  }, [hasCompletedOpenEnrollment, availableBenefitTypes, modifyStep, currentEnrollments, isOpenEnrollmentActive]);

  const isReadOnlyStep = useMemo(() => {
    if (!hasCompletedOpenEnrollment) return false;
    if (isLockedStep) return false;
    if (unlockedModifySteps.has(modifyStep)) return false;
    return true;
  }, [hasCompletedOpenEnrollment, isLockedStep, unlockedModifySteps, modifyStep]);

  // DEV-262: this page had a hand-rolled copy of usePlanPremiumData — its own
  // referencedBasePlanIds memo, two bulk rate queries, and two contribution
  // groupings. The hook produces the same ratesByPlanId/contribsByPlanId maps
  // (for the given plans PLUS any referenced buy-up base plans, whose rates ×
  // percentages define the employer's dollar share) and fails loud on partial
  // loads instead of silently pricing without the base plan. Variable names
  // are kept via destructuring so no consumer below changes.
  const visiblePlanIds = useMemo(
    () => (visiblePlans || []).map((plan: any) => Number(plan.id)),
    [visiblePlans],
  );
  const { ratesByPlanId, contribsByPlanId, isLoading: visiblePlanPremiumsLoading } = usePlanPremiumData(
    visiblePlanIds,
    user?.clientId,
  );

  // Get all selected plan IDs across all benefit types for cost tally and summary
  const allSelectedPlanIds = useMemo(() => {
    return Object.values(selectedModifyPlans)
      .filter((s) => s?.planId)
      .map((s) => s.planId);
  }, [selectedModifyPlans]);

  const { ratesByPlanId: selectedPlanRates, contribsByPlanId: selectedPlanContribs } =
    usePlanPremiumData(allSelectedPlanIds, user?.clientId);

  // Every wizard price call must pass this as ldConfigByPlanId, or
  // life/disability plans price $0.
  const lifeDisabilityPlanIds = useMemo(
    () =>
      ((employeePlansData || []) as any[])
        .filter((p: any) => isLifeDisabilityType(String(p.type || "")))
        .map((p: any) => Number(p.id)),
    [employeePlansData],
  );
  const { ldConfigByPlanId, isLoading: ldConfigsLoading } = useLifeDisabilityConfigs(lifeDisabilityPlanIds);

  // Fetch pending tickets for this employee
  const { data: myPendingTickets = [] } = useQuery({
    queryKey: [
      "/api/tasks",
      { status: "open", employeeId: user?.employeeId },
    ],
    enabled: Boolean(user?.employeeId),
    queryFn: async ({ queryKey }) => {
      const [_key, params] = queryKey as [string, any];
      const url = new URL("/api/tasks", window.location.origin);
      url.searchParams.set("status", "open");
      url.searchParams.set("employeeId", String(user?.employeeId));
      const res = await fetch(url.toString(), { credentials: "include" });
      if (!res.ok) return [] as any[];
      const json = await res.json();
      return json?.tasks || json?.tickets || [];
    },
  });

  // QLE pending broker review (the QLE itself hasn't been approved yet)
  const qlePendingReview = useMemo(() => {
    if (qleJustSubmitted) return true;
    return !!(qualifyingEvents as any)?.pendingEvent;
  }, [qleJustSubmitted, qualifyingEvents]);

  // Most recent denied QLE
  const qleDenied = useMemo(() => {
    return (qualifyingEvents as any)?.deniedEvent || null;
  }, [qualifyingEvents]);

  // Enrollment request pending broker review
  const qleSubmittedPending = useMemo(() => {
    // Show immediately after enrollment submission (before tickets refetch)
    if (qleJustSubmitted) return true;
    // Show only while a request is still awaiting broker approval. An approved
    // request (enrollmentApproved set) has real enrollments — don't treat it as
    // pending even if the task is still open/in_progress on the broker's board.
    return (myPendingTickets as any[]).some(isPendingEnrollmentRequestTask);
  }, [qleJustSubmitted, myPendingTickets]);

  // Fetch beneficiaries for a specific plan
  const { data: beneficiaries = [], refetch: refetchBeneficiaries } = useQuery({
    queryKey: ["/api/employees/benefits/beneficiaries", currentPlanId],
    enabled: !!currentPlanId,
  });

  // Initialize selections with current enrollments when qualifying event is active



  // Handle compare plan checkbox
  const handleComparePlan = (
    categoryType: string,
    planId: number,
    checked: boolean
  ) => {
    setComparePlans((prev) => {
      const currentPlans = prev[categoryType] || [];
      if (checked) {
        // Add plan to comparison if not already included
        if (!currentPlans.includes(planId)) {
          return {
            ...prev,
            [categoryType]: [...currentPlans, planId],
          };
        }
      } else {
        // Remove plan from comparison
        return {
          ...prev,
          [categoryType]: currentPlans.filter((id) => id !== planId),
        };
      }
      return prev;
    });
  };


  // Clear all comparisons for a category
  const clearComparison = (categoryType: string) => {
    setComparePlans((prev) => ({
      ...prev,
      [categoryType]: [],
    }));
    setShowComparison((prev) => ({
      ...prev,
      [categoryType]: false,
    }));
  };


  // DEV-262: removed here — handleOptOut (no callers; wrote the legacy
  // selectedPlans state) and enrollMutation (legacy enrollment submit, only
  // reachable through the signature modal that nothing could open — its
  // onSuccess invalidation of /api/employees/benefits/enrollments never ran;
  // the live modify flow invalidates enrollments-enriched + /api/enrollments
  // in its own onSuccess).


  // Qualifying life event submission mutation
  const qualifyingEventMutation = useMutation({
    mutationFn: async (eventData: z.infer<typeof qualifyingEventSchema>) => {
      const response = await apiRequest(
        "POST",
        "/api/employees/benefits/qualifying-event",
        eventData
      );
      return response.json();
    },
    onSuccess: (data: any) => {
      toast({
        title: "Qualifying Life Event Submitted",
        description: `Your event has been submitted for review. You'll receive confirmation within ${data.reviewPeriod}.`,
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/employees/benefits/qualifying-events"],
      });
      setShowQualifyingEventForm(false);
      form.reset();
    },
    onError: (error: any) => {
      toast({
        title: "Submission Failed",
        description:
          error.message ||
          "Failed to submit qualifying life event. Please try again.",
        variant: "destructive",
      });
    },
  });

  const onSubmitQualifyingEvent = async (
    values: z.infer<typeof qualifyingEventSchema>
  ) => {
    try {
      setQleSubmitting(true);

      // Create the inline dependent draft first (if any). Already-created
      // dependents have cleared the fields, so this never double-submits.
      // A partially-filled draft throws here and aborts the event submit.
      if (qleAllowsDependents) {
        await qleDependentFormRef.current?.flushDraft();
      }

      // Normalize date to ISO (YYYY-MM-DD) to satisfy server parsing across environments
      const normalizeDate = (d: string) => {
        if (!d) return d;
        // Already ISO-like
        if (/^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
        // Convert MM/DD/YYYY -> YYYY-MM-DD
        const m = d.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
        if (m) {
          const mm = m[1].padStart(2, "0");
          const dd = m[2].padStart(2, "0");
          const yyyy = m[3];
          return `${yyyy}-${mm}-${dd}`;
        }
        // Fallback to Date parsing
        const dt = parseDateLocal(d);
        if (dt && !isNaN(dt.getTime())) {
          const yyyy = dt.getFullYear();
          const mm = String(dt.getMonth() + 1).padStart(2, "0");
          const dd = String(dt.getDate()).padStart(2, "0");
          return `${yyyy}-${mm}-${dd}`;
        }
        return d;
      };
      const payload: any = { ...values, eventDate: normalizeDate(values.eventDate) };

      // 1) Upload documentation file if present
      if (qleFile && user?.employeeId) {
        try {
          const formData = new FormData();
          formData.append("document", qleFile);
          formData.append("category", "QLE Documentation");
          formData.append("description", `QLE proof: ${values.eventType} — ${values.description?.slice(0, 100)}`);
          formData.append("tags", JSON.stringify(["qle", values.eventType]));
          const uploadRes = await fetch(`/api/employees/${user.employeeId}/documents/upload`, {
            method: "POST",
            body: formData,
            credentials: "include",
          });
          if (uploadRes.ok) {
            const { document: doc } = await uploadRes.json();
            payload.documentId = doc.id;
          }
        } catch (uploadErr) {
          console.error("QLE doc upload failed (non-blocking):", uploadErr);
        }
      }

      // 2) Create the qualifying life event
      await qualifyingEventMutation.mutateAsync(payload);

      setShowQualifyingEventForm(false);
      form.reset();
      setQleFile(null);

      toast({
        title: "Qualifying life event submitted",
        description: "You can now select your benefits. Your broker will review and approve your enrollment.",
      });

      // Open benefit selection immediately
      setShowModifyBenefitsModal(true);
      setModifyStep(0);
    } catch {
      // errors handled in mutations
    } finally {
      setQleSubmitting(false);
    }
  };


  /**
   * Every benefit type must end in a DECISION — an election or an explicit
   * waiver. A step counts as decided when the employee picked a plan or waived
   * it in this session, or when a decision already exists on file (current or
   * upcoming enrollment, or a standing waiver). Locked/read-only steps are
   * exempt (the employee can't act on them).
   */
  const hasDecisionForType = (type: string): boolean => {
    if ((selectedModifyPlans as any)[type]?.planId > 0) return true;
    if (waivedCoverageByType[type]) return true;
    return (currentEnrollments as any[])?.some((e: any) => {
      if (normalizePlanType(e.planType) !== type) return false;
      if (e.waivedCoverage || String(e.status || "").toLowerCase() === "waived") return true; // standing waiver
      const status = String(e.status || "").toLowerCase();
      if (status !== "active" && status !== "pending") return false;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const ended = e.endDate ? new Date(e.endDate) <= today : false;
      return !ended; // current or upcoming coverage
    });
  };

  const validateDecisionMade = (type: string): boolean => {
    if (hasDecisionForType(type)) return true;
    const label = String(type).charAt(0).toUpperCase() + String(type).slice(1);
    toast({
      title: `${label}: choose a plan or waive coverage`,
      description:
        "Every benefit needs a decision — select a plan to enroll, or choose to waive this coverage, before continuing.",
      variant: "destructive",
    });
    return false;
  };

  const validateWaiverReasons = (currentType: string): boolean => {
    // If employee waived the entire plan, no dependent waiver reasons needed
    if (waivedCoverageByType[currentType]) {
      return true;
    }

    // If no plan selected, no validation needed
    const selectedPlan = selectedModifyPlans[currentType];
    if (!selectedPlan || selectedPlan.planId <= 0) {
      return true;
    }

    // Auto-enrollment plans don't enroll dependents — skip validation
    const planObj = (resolvedPlanSource || []).find((p: any) => p.id === selectedPlan.planId);
    if (Boolean((planObj as any)?.enrollmentRequired)) return true;

    const selectedDependentIds = selectedPlan.dependentIds || [];

    // Find dependents that are waived (not selected for this benefit type).
    // Only consider deps the plan actually covers — Life/AD&D/Disability/FSA
    // hide the dependent selector entirely, so the user has no way to "waive"
    // ineligible deps and shouldn't be blocked by them.
    const eligibleDeps = eligibleDependentsForPlan(planObj, dependents as any[]);
    const waivedDependents = eligibleDeps.filter(
      (d: any) => !selectedDependentIds.includes(d.id)
    );

    // Check if any waived dependents are missing waiver reasons. "Other" also
    // needs a non-empty freeform description.
    const reasonMap = depReasonByType[currentType] ?? {};
    const otherTextMap = depOtherTextByType[currentType] ?? {};
    const missingReasons = waivedDependents.filter((d: any) => {
      const r = reasonMap[d.id];
      if (!r) return true;
      if (r === "other") return !otherTextMap[d.id]?.trim();
      return false;
    });

    if (missingReasons.length > 0) {
      const names = missingReasons.map((d: any) => `${d.firstName} ${d.lastName}`).join(', ');
      toast({
        title: "Waiver Reason Required",
        description: `Please provide a waiver reason for: ${names}`,
        variant: "destructive",
      });
      return false;
    }

    return true;
  };

  // Blocks advancing/finishing when a selected FSA plan's election is missing or
  // out of the plan's allowed range. Hard stop — not just a warning.
  const validateFsaElection = (currentType: string): boolean => {
    if (waivedCoverageByType[currentType]) return true;
    const selected = selectedModifyPlans[currentType];
    if (!selected || selected.planId <= 0) return true;
    const planObj = (resolvedPlanSource as any[]).find((p: any) => p.id === selected.planId);
    if (!isFsaPlanType(String(planObj?.type || ""))) return true;

    const result = validateFsaElectionInput(
      selected.annualElection,
      fsaConfigByPlanId.get(selected.planId),
      employeeRow?.payCycle,
      planObj?.name,
      { payScheduleReady },
    );
    if (!result.ok) {
      toast({ title: "Check your FSA election", description: result.message, variant: "destructive" });
      return false;
    }
    return true;
  };




  const hasCurrentEnrollments = Array.isArray(currentEnrollments)
    ? currentEnrollments.some((e: any) => {
        const status = String(e?.status || "").toLowerCase();
        const isWaived = Boolean(e?.waivedCoverage);
        const ended = e?.endDate
          ? (parseDateLocal(e.endDate as any) ?? new Date(0)) <= new Date()
          : false;
        return status === "active" && !isWaived && !ended;
      })
    : false;
  const hasExistingEnrollments = Array.isArray(currentEnrollments) &&
    (currentEnrollments as any[]).some((e: any) => {
      const st = String(e.status || "").toLowerCase();
      return (st === "active" || st === "pending") && !e.waivedCoverage;
    });

  // Check if a benefit type has an actual change vs current enrollment.
  // Must mirror the initialization logic (OE first-pass by oeProgressKey date, then fallback)
  // so the comparison matches what was pre-populated into selectedModifyPlans / waivedCoverageByType.
  const hasActualChangeForType = (type: string): boolean => {
    const isWaivedType = !!waivedCoverageByType[type];
    const selection = selectedModifyPlans[type];

    // Nothing selected and not waived → no change
    if (!isWaivedType && (!selection || !selection.planId)) return false;

    // Find the enrollment that the initialization logic would have used for this type
    // (mirrors the two-pass logic in the "Modify Elections" button onClick)
    const allForType = (currentEnrollments as any[])?.filter(
      (e: any) => String(e.planType || "").toLowerCase() === type
    ) || [];

    let matchedEnrollment: any = null;
    let matchedIsWaived = false;

    // First pass: OE enrollments matching oeProgressKey date
    const oeEffDateStr = oeProgressKey ? String(oeProgressKey).split('T')[0] : null;
    if (isOpenEnrollmentActive && oeEffDateStr) {
      for (const e of allForType) {
        const src = String(e.enrollmentSource || "").toLowerCase();
        if (src !== "open_enrollment") continue;
        if (!e.effectiveDate || String(e.effectiveDate).split('T')[0] !== oeEffDateStr) continue;
        if (e.waivedCoverage || String(e.status || "").toLowerCase() === "waived") {
          matchedEnrollment = e;
          matchedIsWaived = true;
          break;
        } else if (isActiveEnrollment(e) || String(e.status || "").toLowerCase() === "pending") {
          matchedEnrollment = e;
          matchedIsWaived = false;
          break;
        }
      }
    }

    // Second pass: any active/pending (fallback)
    if (!matchedEnrollment) {
      for (const e of allForType) {
        if (e.waivedCoverage || String(e.status || "").toLowerCase() === "waived") {
          matchedEnrollment = e;
          matchedIsWaived = true;
          break;
        } else if (isActiveEnrollment(e) || String(e.status || "").toLowerCase() === "pending") {
          matchedEnrollment = e;
          matchedIsWaived = false;
          break;
        }
      }
    }

    if (isWaivedType) {
      // Waiving is only a change if the matched enrollment wasn't already waived
      return !matchedIsWaived;
    }

    // Plan selected
    if (selection && selection.planId) {
      if (!matchedEnrollment || matchedIsWaived) {
        // No current plan enrollment (or was waived) → selecting a plan is a change
        return true;
      }
      if (matchedEnrollment.planId !== selection.planId) {
        return true;
      }
      // Same plan → check dependents
      const currentDeps = [...(matchedEnrollment.enrolledDependents || [])].sort((a: number, b: number) => a - b);
      const newDeps = [...(selection.dependentIds || [])].sort((a: number, b: number) => a - b);
      if (currentDeps.length !== newDeps.length) return true;
      return currentDeps.some((id: number, i: number) => id !== newDeps[i]);
    }

    return false;
  };

  // Summary Page Component
  const SummaryPage = () => {
    // Use available benefit types from the parent scope
    const benefitTypes = availableBenefitTypes.length > 0
      ? availableBenefitTypes
      : ENROLLABLE_BENEFIT_TYPE_ORDER;

    // Per-election benefit detail (one fetch fan-out, type-aware). Resolved here
    // — before any early return — so the hook order stays stable. The plan's
    // real `type` drives which config endpoint is read.
    const benefitDetailElections = useMemo(() => {
      return benefitTypes
        .map((type) => {
          const selection = selectedModifyPlans[type];
          if (!selection || !selection.planId) return null;
          const plan =
            (employeeAllPlans as any[])?.find((p: any) => p.id === selection.planId) ||
            (employeePlansData as any[])?.find((p: any) => p.id === selection.planId);
          if (!plan) return null;
          return { planId: selection.planId as number, type: String(plan.type || type) };
        })
        .filter(Boolean) as { planId: number; type: string }[];
    }, [benefitTypes, selectedModifyPlans, employeeAllPlans, employeePlansData]);

    const benefitDetailByPlanId = useBenefitDetailData(benefitDetailElections);

    const hasAnyChange = benefitTypes.some((type) => hasActualChangeForType(type));

    // No actual changes — show a simple message and go back
    if (!hasAnyChange) {
      return (
        <div className="w-full px-6 py-6 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                No Changes to Submit
              </CardTitle>
              <p className="text-gray-600">
                Your elections are the same as your current enrollment. No action is needed.
              </p>
            </CardHeader>
            <CardContent>
              <div className="flex justify-end pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => setShowSummaryPage(false)}
                >
                  Back to Elections
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    // Calculate costs from current enrollments
    const currentBenefits = Array.isArray(currentEnrollments)
      ? currentEnrollments
      : [];
    const totalCurrentCost = currentBenefits.reduce(
      (sum: number, benefit: any) => sum + (benefit.monthlyPremium || 0),
      0
    );

    // Calculate new costs from modifications
    let totalNewCost = 0;

    benefitTypes.forEach((type) => {
      const selection = selectedModifyPlans[type];
      if (selection && selection.planId) {
        // Check both employeeAllPlans and employeePlansData for the plan
        const plan = (employeeAllPlans as any[])?.find((p: any) => p.id === selection.planId) ||
                     (employeePlansData as any[])?.find((p: any) => p.id === selection.planId);
        if (plan) {
          totalNewCost += calculatePremiumForSelection({
            plan,
            ratesByPlanId: selectedPlanRates as any,
            contribsByPlanId: selectedPlanContribs as any,
            employee: employeeRow,
            selectedDependentIds: selection.dependentIds,
            allDependents: dependents as any[],
            ldConfigByPlanId,
          });
        }
      }
    });

    return (
      <div className="w-full px-6 py-6 space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Enrollment Summary
            </CardTitle>
            <p className="text-gray-600">
              Review your benefit changes before signing
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Cost Comparison */}
            <div className="grid grid-cols-3 gap-4 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-600">
                  ${hasNonMonthlyPayCycle(employeeRow?.payCycle)
                    ? calculatePerPaycheckAmount(totalCurrentCost, employeeRow?.payCycle).toFixed(2)
                    : totalCurrentCost.toFixed(2)}
                </div>
                <div className="text-sm text-gray-500">
                  Current {hasNonMonthlyPayCycle(employeeRow?.payCycle)
                    ? getPayCycleLabelCapitalized(employeeRow?.payCycle)
                    : "Monthly"} Cost
                </div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  ${hasNonMonthlyPayCycle(employeeRow?.payCycle)
                    ? calculatePerPaycheckAmount(totalNewCost, employeeRow?.payCycle).toFixed(2)
                    : totalNewCost.toFixed(2)}
                </div>
                <div className="text-sm text-gray-500">
                  New {hasNonMonthlyPayCycle(employeeRow?.payCycle)
                    ? getPayCycleLabelCapitalized(employeeRow?.payCycle)
                    : "Monthly"} Cost
                </div>
              </div>
              <div className="text-center">
                <div
                  className={`text-2xl font-bold ${
                    totalNewCost > totalCurrentCost
                      ? "text-red-600"
                      : "text-green-600"
                  }`}
                >
                  {totalNewCost > totalCurrentCost ? "+" : ""}$
                  {hasNonMonthlyPayCycle(employeeRow?.payCycle)
                    ? calculatePerPaycheckAmount(totalNewCost - totalCurrentCost, employeeRow?.payCycle).toFixed(2)
                    : (totalNewCost - totalCurrentCost).toFixed(2)}
                </div>
                <div className="text-sm text-gray-500">
                  {hasNonMonthlyPayCycle(employeeRow?.payCycle)
                    ? getPayCycleLabelCapitalized(employeeRow?.payCycle)
                    : "Monthly"} Difference
                </div>
              </div>
            </div>

            {/* Plan Changes */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Plan Changes Summary</h3>
              {!benefitTypes.some((type) => hasActualChangeForType(type)) ? (
                <p className="text-gray-500 italic">
                  No changes made to benefits
                </p>
              ) : (
                benefitTypes.map((type) => {
                  const isWaivedType = waivedCoverageByType[type];
                  const selection = selectedModifyPlans[type];
                  if (!isWaivedType && (!selection || !selection.planId)) return null;
                  if (!hasActualChangeForType(type)) return null;

                  const typeLabelsLocal: Record<string, string> = {
                    health: "Medical", dental: "Dental", vision: "Vision",
                    life: "Life", disability: "Disability",
                  };

                  // Waived benefit type — render a simple waived card
                  if (isWaivedType) {
                    return (
                      <Card key={type} className="p-4">
                        <h4 className="font-semibold text-lg mb-3 capitalize border-b pb-2">
                          {typeLabelsLocal[type] || getPlanTypeMeta(type).label} Insurance
                        </h4>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">Coverage Decision</span>
                          <Badge variant="outline" className="bg-orange-100 text-orange-800 border-orange-300">
                            Waived
                          </Badge>
                        </div>
                        {waiverReasonByType[type] && (
                          <p className="text-sm text-muted-foreground mt-2">
                            Reason: {waiverReasonByType[type]}
                          </p>
                        )}
                      </Card>
                    );
                  }

                  // Find the NEW plan from employee's available plans
                  const newPlan = (employeeAllPlans as any[])?.find(
                    (p: any) => p.id === selection.planId
                  ) || (employeePlansData as any[])?.find(
                    (p: any) => p.id === selection.planId
                  );
                  if (!newPlan) return null;

                  const dependentsList = Array.isArray(dependents) ? dependents : [];

                  // Find enrollments for this benefit type that are CURRENTLY in effect.
                  // Reuse the canonical isCurrentlyEffectiveEnrollment (excludes waived,
                  // ended, and future-effective; keeps "terminated but endDate still in
                  // the future" so a pending-termination window still shows).
                  // Waived/pending are kept regardless of effective date — they represent
                  // the employee's current decision even if it takes effect in the future.
                  const enrollmentsForType = (currentEnrollments as any[])?.filter(
                    (e: any) =>
                      String(e.planType || "").toLowerCase() === type &&
                      (isCurrentlyEffectiveEnrollment(e) || e.waivedCoverage || String(e.status || "").toLowerCase() === "waived" || String(e.status || "").toLowerCase() === "pending")
                  ) || [];

                  // Among current enrollments, check for waived first (represents previous state)
                  const waivedEnrollment = enrollmentsForType.find(
                    (e: any) => e.waivedCoverage || String(e.status || "").toLowerCase() === "waived"
                  );

                  // Check for pending enrollment (future effective date)
                  const pendingEnrollment = enrollmentsForType.find(
                    (e: any) => !e.waivedCoverage && String(e.status || "").toLowerCase() === "pending"
                  );

                  // Then check for active enrollment
                  const activeEnrollment = enrollmentsForType.find(
                    (e: any) => !e.waivedCoverage && String(e.status || "").toLowerCase() !== "waived" && String(e.status || "").toLowerCase() !== "pending"
                  );

                  // Prioritize: waived > pending > active
                  const currentEnrollment = waivedEnrollment || pendingEnrollment || activeEnrollment;
                  const isCurrentlyWaived = !!waivedEnrollment;
                  const isCurrentlyPending = !isCurrentlyWaived && !!pendingEnrollment;
                  const hasCurrentEnrollment = !!activeEnrollment && !isCurrentlyWaived && !isCurrentlyPending;

                  // Current enrollment details
                  const currentPlanName = currentEnrollment?.planName || "None";
                  const currentPremium = (hasCurrentEnrollment || isCurrentlyPending) ? Number(currentEnrollment?.monthlyPremium || 0) : 0;
                  const currentCoverageTier = currentEnrollment?.coverageLevel || "employee_only";
                  const currentDependentIds = currentEnrollment?.enrolledDependents || [];
                  const currentDependents = dependentsList.filter((d: any) =>
                    currentDependentIds.includes(d.id)
                  );

                  // New selection details
                  const newEnrolledDependents = dependentsList.filter((d: any) =>
                    selection.dependentIds.includes(d.id)
                  );
                  const newWaivedDependents = dependentsList.filter(
                    (d: any) => !selection.dependentIds.includes(d.id)
                  );
                  const newPremium = calculatePremiumForSelection({
                    plan: newPlan,
                    ratesByPlanId: selectedPlanRates as any,
                    contribsByPlanId: selectedPlanContribs as any,
                    employee: employeeRow,
                    selectedDependentIds: selection.dependentIds,
                    allDependents: dependentsList,
                    ldConfigByPlanId,
                  });

                  // Determine coverage tier label
                  const formatTier = (tier: string) => {
                    return tier
                      .replace("employee_only", "Employee Only")
                      .replace("employee_spouse", "Employee + Spouse")
                      .replace("employee_children", "Employee + Children")
                      .replace("family", "Family")
                      .replace(/_/g, " ");
                  };

                  const newCoverageTier = (() => {
                    const hasSpouse = newEnrolledDependents.some(
                      (d: any) => String(d.relationship).toLowerCase() === "spouse"
                    );
                    const hasChildren = newEnrolledDependents.some(
                      (d: any) => String(d.relationship).toLowerCase().includes("child")
                    );
                    if (hasSpouse && hasChildren) return "family";
                    if (hasSpouse) return "employee_spouse";
                    if (hasChildren) return "employee_children";
                    return "employee_only";
                  })();

                  const typeLabels: Record<string, string> = {
                    health: "Medical",
                    dental: "Dental",
                    vision: "Vision",
                    life: "Life",
                    disability: "Disability",
                  };

                  return (
                    <Card key={type} className="p-4">
                      <h4 className="font-semibold text-lg mb-4 capitalize border-b pb-2">
                        {typeLabels[type] || getPlanTypeMeta(type).label} Insurance
                      </h4>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Previous Status */}
                        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 flex flex-col">
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                              Previous Status
                            </span>
                            {!currentEnrollment ? (
                              <Badge variant="outline" className="bg-gray-100 text-gray-600 border-gray-300">
                                None
                              </Badge>
                            ) : isCurrentlyWaived ? (
                              <Badge variant="outline" className="bg-orange-100 text-orange-800 border-orange-300">
                                Waived
                              </Badge>
                            ) : isCurrentlyPending ? (
                              <Badge variant="outline" className="bg-yellow-100 text-yellow-800 border-yellow-300">
                                Pending
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="bg-green-100 text-green-800 border-green-300">
                                Active
                              </Badge>
                            )}
                          </div>

                          {!currentEnrollment ? (
                            <div className="flex-1 flex items-center justify-center py-4">
                              <p className="text-sm text-gray-400 italic">No previous enrollment</p>
                            </div>
                          ) : isCurrentlyWaived ? (
                            <div className="flex-1">
                              <p className="text-base font-semibold text-gray-800 dark:text-gray-200 mb-3">
                                {currentPlanName}
                              </p>
                              <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-gray-500">Premium</span>
                                  <span className="font-medium text-gray-700 dark:text-gray-300">$0.00/mo</span>
                                </div>
                              </div>
                            </div>
                          ) : isCurrentlyPending ? (
                            <div className="flex-1">
                              <p className="text-base font-semibold text-gray-800 dark:text-gray-200 mb-3">
                                {currentPlanName}
                              </p>
                              <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-gray-500">Coverage</span>
                                  <span className="font-medium text-gray-700 dark:text-gray-300">{formatTier(currentCoverageTier)}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-500">Premium</span>
                                  <span className="font-medium text-gray-700 dark:text-gray-300">${currentPremium.toFixed(2)}/mo</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-500">Effective Date</span>
                                  <span className="font-medium text-gray-700 dark:text-gray-300">
                                    {currentEnrollment?.effectiveDate ? formatDateDisplay(currentEnrollment.effectiveDate) : "—"}
                                  </span>
                                </div>
                              </div>
                              {currentDependents.length > 0 && (
                                <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                                  <p className="text-xs font-medium text-gray-500 mb-1">Covered Dependents</p>
                                  <div className="space-y-1">
                                    {currentDependents.map((d: any) => (
                                      <p key={d.id} className="text-xs text-gray-600 dark:text-gray-400">
                                        {d.firstName} {d.lastName}
                                      </p>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="flex-1">
                              <p className="text-base font-semibold text-gray-800 dark:text-gray-200 mb-3">
                                {currentPlanName}
                              </p>
                              <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-gray-500">Coverage</span>
                                  <span className="font-medium text-gray-700 dark:text-gray-300">{formatTier(currentCoverageTier)}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-500">Premium</span>
                                  <span className="font-medium text-gray-700 dark:text-gray-300">${currentPremium.toFixed(2)}/mo</span>
                                </div>
                              </div>
                              {currentDependents.length > 0 && (
                                <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                                  <p className="text-xs font-medium text-gray-500 mb-1">Covered Dependents</p>
                                  <div className="space-y-1">
                                    {currentDependents.map((d: any) => (
                                      <p key={d.id} className="text-xs text-gray-600 dark:text-gray-400">
                                        {d.firstName} {d.lastName}
                                      </p>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        {/* New Selection */}
                        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 flex flex-col border-2 border-blue-200 dark:border-blue-800">
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-xs font-medium uppercase tracking-wide text-blue-600 dark:text-blue-400">
                              New Selection
                            </span>
                            <Badge variant="outline" className="bg-blue-100 text-blue-800 border-blue-300">
                              Enrolling
                            </Badge>
                          </div>

                          <div className="flex-1">
                            <p className="text-base font-semibold text-gray-800 dark:text-gray-200 mb-3">
                              {newPlan.name}
                            </p>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-gray-500">Coverage</span>
                                <span className="font-medium text-gray-700 dark:text-gray-300">{formatTier(newCoverageTier)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500">Premium</span>
                                <span className="font-semibold text-blue-600 dark:text-blue-400">${newPremium.toFixed(2)}/mo</span>
                              </div>
                              {(() => {
                                const selPlan = (resolvedPlanSource as any[]).find((p: any) => p.id === selection.planId);
                                // Find the OE enrollment's effective date (matching oeProgressKey),
                                // falling back to the pending/active enrollment's date
                                const oeEffStr = oeProgressKey ? String(oeProgressKey).split('T')[0] : null;
                                const oeEnrollmentForType = (isOpenEnrollmentActive && oeEffStr)
                                  ? enrollmentsForType.find((e: any) =>
                                      String(e.enrollmentSource || "").toLowerCase() === "open_enrollment" &&
                                      e.effectiveDate && String(e.effectiveDate).split('T')[0] === oeEffStr &&
                                      !e.waivedCoverage && String(e.status || "").toLowerCase() !== "waived"
                                    )
                                  : null;
                                const existingEffDate = (oeEnrollmentForType || pendingEnrollment || activeEnrollment)?.effectiveDate;
                                // Otherwise show the same date the submit will save (shared
                                // resolver) — renewalDate is the NEXT plan year's start, so it
                                // must not win over a future plan effectiveDate.
                                const planEffDate = enrollmentState === "open_enrollment" && existingEffDate
                                  ? existingEffDate
                                  : resolveEnrollmentEffectiveDate({
                                      enrollmentState,
                                      plan: selPlan,
                                      hasCompletedOpenEnrollment,
                                      oeProgressKey,
                                      qleEventDate,
                                      qleEffectiveDate: qleEffectiveDate ? qleEffectiveDate.toISOString() : null,
                                      newHireEffectiveDate: eligibilityByPlanId.get(selection.planId)?.effectiveDate ?? null,
                                    });
                                if (!planEffDate) return null;
                                return (
                                  <div className="flex justify-between">
                                    <span className="text-gray-500">Effective</span>
                                    <span className="font-medium text-gray-700 dark:text-gray-300">
                                      {formatDateDisplay(planEffDate)}
                                    </span>
                                  </div>
                                );
                              })()}
                            </div>

                            {(newEnrolledDependents.length > 0 || newWaivedDependents.length > 0) && (
                              <div className="mt-3 pt-3 border-t border-blue-200 dark:border-blue-800">
                                {newEnrolledDependents.length > 0 && (
                                  <div className="mb-2">
                                    <p className="text-xs font-medium text-green-600 mb-1">Enrolling Dependents</p>
                                    <div className="space-y-1">
                                      {newEnrolledDependents.map((d: any) => (
                                        <p key={d.id} className="text-xs text-gray-600 dark:text-gray-400">
                                          {d.firstName} {d.lastName} <span className="text-gray-400">({d.relationship})</span>
                                        </p>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                {newWaivedDependents.length > 0 && (
                                  <div>
                                    <p className="text-xs font-medium text-orange-600 mb-1">Waiving Dependents</p>
                                    <div className="space-y-1">
                                      {newWaivedDependents.map((d: any) => (
                                        <p key={d.id} className="text-xs text-gray-600 dark:text-gray-400">
                                          {d.firstName} {d.lastName} <span className="text-gray-400">({d.relationship})</span>
                                          {(() => {
                                            const r = depReasonByType[type]?.[d.id];
                                            if (!r) return null;
                                            const label = r === "other"
                                              ? (depOtherTextByType[type]?.[d.id] || "Other")
                                              : (WAIVE_REASONS.find((w) => w.value === r)?.label ?? r);
                                            return <span className="italic text-gray-400"> — {label}</span>;
                                          })()}
                                        </p>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Benefit Details — type-aware (medical/dental/vision,
                          life/disability, supplemental, FSA), collapsed by default */}
                      <CollapsibleBenefitDetails
                        plan={newPlan}
                        detailData={benefitDetailByPlanId.get(selection.planId)}
                        annualElection={
                          selection.annualElection != null && selection.annualElection !== ""
                            ? Number(selection.annualElection)
                            : null
                        }
                      />

                      {/* Cost Impact */}
                      <div className="mt-4 pt-3 border-t flex justify-between items-center">
                        <span className="text-sm text-gray-600">Cost Impact:</span>
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500">${currentPremium.toFixed(2)}</span>
                          <ChevronRight className="w-4 h-4 text-gray-400" />
                          <span className="font-semibold text-blue-600 dark:text-blue-400">${newPremium.toFixed(2)}</span>
                          <span className={`text-sm font-medium ${
                            newPremium > currentPremium ? "text-red-600" :
                            newPremium < currentPremium ? "text-green-600" : "text-gray-500"
                          }`}>
                            ({newPremium >= currentPremium ? "+" : ""}${(newPremium - currentPremium).toFixed(2)})
                          </span>
                        </div>
                      </div>
                    </Card>
                  );
                })
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => setShowSummaryPage(false)}
              >
                Back to Enrollment
              </Button>
              <Button
                className="bg-blue-600 hover:bg-blue-700"
                onClick={() => {
                  setShowSummaryPage(false);
                  setShowSignaturePage(true);
                }}
              >
                Proceed to Signature
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  // Submit enrollment with forms and signature
  const submitEnrollment = async (formData: any) => {
    if (!formReviewData) throw new Error("Missing form review data");
    
    const ctx = formReviewData.enrollmentContext;
    
    // Helper: derive coverage tier from selected dependents
    const computeTier = (depIds: number[]): string => {
      const deps = (
        Array.isArray(ctx.dependents) ? (ctx.dependents as any[]) : []
      ).filter((d: any) => depIds.includes(d.id));
      const spouseRels = ["spouse", "domestic_partner", "domestic partner"];
      const hasSpouse = deps.some(
        (d: any) => spouseRels.includes(String(d.relationship).toLowerCase())
      );
      const hasChild = deps.some((d: any) =>
        String(d.relationship).toLowerCase().startsWith("child")
      );
      if (hasSpouse && hasChild) return "family";
      if (hasSpouse) return "employee_spouse";
      if (hasChild) return "employee_children";
      return "employee_only";
    };

    const effectiveDate =
      ctx.enrollmentState === "qualifying_event" && ctx.qleEffectiveDate
        ? ctx.qleEffectiveDate.toISOString()
        : new Date().toISOString();

    const entries = Object.entries(ctx.selectedModifyPlans);
    const sourcePlans = resolvedPlanSource as any[];

    // Resolve the coverage effective date for a plan. Returns null when it can't
    // be determined — we never silently default to today.
    const resolveEffectiveFor = (planId: number): string | null =>
      resolveEnrollmentEffectiveDate({
        enrollmentState: ctx.enrollmentState,
        plan: (sourcePlans || []).find((p: any) => p.id === planId),
        hasCompletedOpenEnrollment: ctx.hasCompletedOpenEnrollment,
        oeProgressKey: ctx.oeProgressKey,
        qleEventDate: ctx.qleEventDate,
        qleEffectiveDate: ctx.qleEffectiveDate ? ctx.qleEffectiveDate.toISOString() : null,
        newHireEffectiveDate: eligibilityByPlanId.get(planId)?.effectiveDate ?? null,
      });

    // Guard: if any selected plan or waiver can't resolve a real effective date,
    // block the whole submit rather than POST a made-up date.
    const planIdsToResolve = [
      ...entries.filter(([, s]) => s?.planId).map(([, s]) => s!.planId as number),
      ...(ctx.waiversList || []).map((w: any) => w.planId as number),
    ];
    const unresolvedPlanIds = planIdsToResolve.filter((id) => !resolveEffectiveFor(id));
    if (unresolvedPlanIds.length) {
      const unresolvedNames = unresolvedPlanIds
        .map((id) => (sourcePlans || []).find((p: any) => p.id === id)?.name || `plan #${id}`)
        .join(", ");
      console.error(
        "[Benefits] submit blocked — no effective date for plans:",
        unresolvedPlanIds,
        "(no effective/renewal date on or after today)",
      );
      toast({
        title: "Couldn't determine your coverage start date",
        description: `No coverage start date could be computed for: ${unresolvedNames}. The plan needs an effective or renewal date on or after today — contact your administrator.`,
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSaving(true);
      // 1. Create signature first
      const sigRes = await fetch("/api/signatures", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          signature: {
            name: formData.signatureName,
            data: formData.signatureData,
            timestamp: new Date().toISOString(),
          },
          employeeId: ctx.employeeId,
          clientId: ctx.clientId,
          signatureType: withinOpenEnrollment ? "open_enrollment" : "qle",
          signedContent: {
            enrollments: entries
              .filter(([, sel]) => sel?.planId)
              .map(([, sel]) => ({
                ...sel,
                // Per-plan coverage start (guard above guarantees non-null) —
                // lets the server build one summary notification per submission.
                effectiveDate: resolveEffectiveFor(sel!.planId as number),
              })),
            waivers: ctx.waiversList,
            effectiveDate,
            // Marks the direct-elections path (vs QLE request-enrollment) so
            // the server knows enrollments are being created for this signature.
            submitFlow: "elections",
            enrollmentSource: ctx.enrollmentState,
            isModification: !!ctx.hasCompletedOpenEnrollment,
            forms: formData.forms,
            // Forms whose signature couldn't be collected (carrier form failed
            // to prepare) — audit trail + hook for admin follow-up.
            pendingForms: formData.pendingForms ?? [],
          },
        }),
      });
      if (!sigRes.ok) {
        const err = await sigRes.json().catch(() => ({}));
        throw new Error(err?.message || "Failed to save signature");
      }
      const { signatureId } = await sigRes.json();

      if (withinOpenEnrollment || ctx.enrollmentState === "new_hire") {
        // 2a. If modifying, clean up old OE enrollments first
        if (ctx.hasCompletedOpenEnrollment) {
          const covDate = ctx.oeProgressKey;
          if (covDate) {
            const cleanupRes = await fetch(
              `/api/employees/${ctx.employeeId}/enrollments/oe?startDate=${encodeURIComponent(covDate)}`,
              { method: "DELETE" }
            );
            if (!cleanupRes.ok) {
              const err = await cleanupRes.json().catch(() => ({}));
              throw new Error(err?.message || "Failed to clean up previous enrollments");
            }
          }
        }

        // 2b. Create enrollments with signatureId
        await Promise.all(
          entries.map(async ([benefitType, selection]) => {
            if (!selection?.planId) return;
            const matchedPlan = (sourcePlans || []).find(
              (p: any) => p.id === selection.planId
            );
            // Guaranteed non-null by the pre-submit guard above.
            const planEffectiveDate = resolveEffectiveFor(selection.planId)!;
            // FSA is employee-only (IRS): no dependents/tier, carries the annual election.
            const selectionIsFsa = isFsaPlanType(String(matchedPlan?.type || ""));
            const wantsBeneficiaries = requiresBeneficiary(String(matchedPlan?.type || ""));
            const payload = buildEnrollmentPayload({
              employeeId: ctx.employeeId,
              planId: selection.planId,
              clientId: ctx.clientId,
              coverageTier: selectionIsFsa ? "employee_only" : computeTier(selection.dependentIds || []),
              dependentIds: selectionIsFsa ? [] : (selection.dependentIds || []),
              ...(wantsBeneficiaries ? mirrorPrimaryToLegacy(selection.beneficiaries) : {}),
              enrollmentSource: ctx.enrollmentState === "new_hire" ? "new_hire" : "open_enrollment",
              effectiveDate: planEffectiveDate,
              signatureId,
              annualElection:
                selectionIsFsa && selection.annualElection
                  ? String(Number(selection.annualElection))
                  : undefined,
              dependentWaiverReasons: matchedPlan?.enrollmentRequired
                ? (() => {
                    const allDepIds = (ctx.dependents || []).map((d: any) => d.id);
                    const enrolled = selection.dependentIds || [];
                    const waived = allDepIds.filter((id: number) => !enrolled.includes(id));
                    if (waived.length === 0) return null;
                    return waived.reduce((acc: Record<string, string>, id: number) => { acc[String(id)] = "Auto-enrollment"; return acc; }, {});
                  })()
                : ctx.waiveReasons && ctx.waiveReasons[benefitType] && Object.keys(ctx.waiveReasons[benefitType]).length > 0 ? ctx.waiveReasons[benefitType] : null,
            });
            const res = await fetch(
              `/api/employees/${ctx.employeeId}/enrollments`,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
              }
            );
            if (!res.ok) {
              const err = await res.json().catch(() => ({}));
              throw new Error(err?.message || "Failed to save enrollment");
            }
            const created = await res.json().catch(() => ({}));
            // Persist the full beneficiary set against the created base enrollment.
            if (wantsBeneficiaries && selection.beneficiaries && created?.id) {
              await saveBeneficiaries(created.id, selection.beneficiaries, {
                employeeId: ctx.employeeId,
                planId: selection.planId,
              });
            }
            return created;
          })
        );

        // 3. Create waivers with signatureId
        await Promise.all(
          ctx.waiversList.map(async (waiver: any) => {
            // Guaranteed non-null by the pre-submit guard above.
            const waiverEffectiveDate = resolveEffectiveFor(waiver.planId)!;
            const waiverToday = new Date();
            waiverToday.setHours(0, 0, 0, 0);
            const waiverEff = waiverEffectiveDate ? new Date(waiverEffectiveDate) : null;
            if (waiverEff) waiverEff.setHours(0, 0, 0, 0);
            const waiverStatus = waiverEff && waiverEff > waiverToday ? "pending" : "active";
            const payload = buildEnrollmentPayload({
              employeeId: ctx.employeeId,
              planId: waiver.planId,
              clientId: ctx.clientId,
              coverageTier: "employee_only",
              dependentIds: [],
              enrollmentSource: ctx.enrollmentState === "new_hire" ? "new_hire" : "open_enrollment",
              effectiveDate: waiverEffectiveDate,
              waivedCoverage: true,
              waiverReason: waiver.waiverReason || null,
              status: waiverStatus,
              signatureId,
            });
            const res = await fetch(
              `/api/employees/${ctx.employeeId}/enrollments`,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
              }
            );
            if (!res.ok) {
              const err = await res.json().catch(() => ({}));
              throw new Error(err?.message || "Failed to save waiver");
            }
            return res.json();
          })
        );
      } else {
        // Outside OE: create a ticket for broker review (QLE enrollment request)
        const requested: Record<string, any> = {};
        entries.forEach(([, sel]) => {
          if (!sel?.planId) return;
          const plan = (employeeAllPlans as any[]).find(
            (p: any) => p.id === sel.planId
          );
          const benefitType = plan?.type || "health";
          const reqIsFsa = isFsaPlanType(String(plan?.type || ""));
          requested[benefitType] = {
            planId: sel.planId,
            coverageTier: reqIsFsa ? "employee_only" : computeTier(sel.dependentIds || []),
            dependentIds: reqIsFsa ? [] : (sel.dependentIds || []),
            ...(reqIsFsa && sel.annualElection ? { annualElection: String(Number(sel.annualElection)) } : {}),
          };
        });
        const enrollRes = await fetch(`/api/enrollments/request-enrollment`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            enrollments: requested,
            waivers: ctx.waiversList,
            qleEventId:
              (qualifyingEvents as any)?.activeEvent?.id || undefined,
            effectiveDate,
            signatureId,
          }),
        });
        if (!enrollRes.ok) {
          const err = await enrollRes.json().catch(() => ({}));
          throw new Error(err?.message || "Failed to submit enrollment request");
        }

        // Track QLE submission for pending banner (immediate display before tickets refetch)
        if ((qualifyingEvents as any)?.activeEvent?.id) {
          setQleJustSubmitted(true);
        }
      }

      // Upload client-generated filled PDFs (match exactly what the preview shows)
      if (formData.generatedPdfs && formData.generatedPdfs.length > 0) {
        for (const pdf of formData.generatedPdfs) {
          const blob = new Blob([pdf.pdfBytes], { type: "application/pdf" });
          const fileName = `${pdf.formName}-${employeeRow?.lastName || "employee"}-filled.pdf`;
          const uploadFormData = new FormData();
          uploadFormData.append("document", blob, fileName);
          uploadFormData.append("category", "enrollment");
          uploadFormData.append("description", `Enrollment form: ${pdf.formName}`);
          uploadFormData.append("tags", JSON.stringify(["enrollment", "form"]));

          const uploadRes = await fetch(
            `/api/clients/${ctx.clientId}/employees/${ctx.employeeId}/documents/upload`,
            {
              method: "POST",
              body: uploadFormData,
            }
          );
          if (!uploadRes.ok) {
            const err = await uploadRes.json().catch(() => ({}));
            console.error("Failed to upload filled form PDF:", err);
          }
        }
      } else if (formData.forms && formData.forms.length > 0) {
        // Fallback: server-side generation
        const docsRes = await fetch(
          `/api/employees/${ctx.employeeId}/documents/form-pdfs`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              clientId: ctx.clientId,
              forms: formData.forms,
              signatureName: formData.signatureName,
              signatureData: formData.signatureData,
            }),
          }
        );
        if (!docsRes.ok) {
          const err = await docsRes.json().catch(() => ({}));
          throw new Error(err?.message || "Failed to save enrollment documents");
        }
      }

      // Mark new hire enrollment as completed (triggers task + notification for broker)
      if (ctx.enrollmentState === "new_hire" && ctx.employeeId) {
        try {
          await fetch(`/api/employees/${ctx.employeeId}/new-hire-enrollment/complete`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
          });
        } catch (e) {
          console.warn("Failed to mark new hire enrollment complete:", e);
        }
      }

      toast({
        title: "Enrollment submitted",
        description:
          "Your benefit elections are pending broker approval. You'll be notified when they're processed." +
          (formData.pendingForms?.length
            ? " One form still needs your signature — your administrator will follow up."
            : ""),
      });

      // Enrollment submitted — discard the saved draft so it can't resurface.
      clearEnrollmentDraft();

      // Reset all states
      setShowSignaturePage(false);
      setShowModifyBenefitsModal(false);
      setShowFormReview(false);
      setFormReviewData(null);
      setSelectedModifyPlans({});
      setDepReasonByType({});
      setDepOtherTextByType({});
      setWaivedCoverageByType({});
      setWaiverReasonByType({});
      setWaiverDescriptionByType({});
      setModifyStep(0);

      // Refresh data
      queryClient.invalidateQueries({
        queryKey: [
          "/api/employees",
          user?.employeeId,
          "enrollments-enriched",
          user?.clientId,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/enrollments"],
      });
      // Refresh OE progress so button updates to "Modify Elections"
      queryClient.invalidateQueries({
        queryKey: [
          "/api/clients",
          user?.clientId,
          "open-enrollment",
          "progress",
        ],
      });
      // Refresh pending tickets so QLE banner updates after data refetch
      queryClient.invalidateQueries({
        queryKey: ["/api/tasks"],
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error && error.message
          ? error.message
          : "Failed to save enrollment. Please try again.",
        variant: "destructive",
      });
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  // Signature Page Component
  const SignaturePageComponent = () => {
    const [localSignatureName, setLocalSignatureName] = useState("");
    const [localSignatureData, setLocalSignatureData] = useState<string | null>(
      null
    );
    const localSignatureRef = useRef<SignatureCanvas>(null);

    const handleSignatureEnd = () => {
      if (!localSignatureRef.current) return;
      setLocalSignatureData(localSignatureRef.current.toDataURL());
    };

    const clearSignature = () => {
      if (!localSignatureRef.current) return;
      localSignatureRef.current.clear();
      setLocalSignatureData(null);
    };

    const completeEnrollment = async () => {
      if (!localSignatureName.trim()) {
        toast({
          title: "Error",
          description: "Please enter your full name.",
          variant: "destructive",
        });
        return;
      }

      if (!localSignatureData) {
        toast({
          title: "Error",
          description: "Please provide your digital signature.",
          variant: "destructive",
        });
        return;
      }

      // Validate every selected FSA plan (combined employer + employee ≤ cap).
      // Reuses the single validator so the rule matches the card + per-step gate.
      for (const type of Object.keys(selectedModifyPlans)) {
        if (!validateFsaElection(type)) return;
      }

      try {
        setIsSaving(true);

        // Collect enrollment context
        const employeeId = employeeRow?.id;
        const clientId = user?.clientId;
        if (!employeeId || !clientId)
          throw new Error("Missing employee context");

        // Build waivers list
        const waivedTypes = Object.entries(waivedCoverageByType).filter(
          ([, v]) => !!v
        );
        const sourcePlans = resolvedPlanSource as any[];
        const waiversList = waivedTypes
          .map(([benefitType]) => {
            const plan = (sourcePlans || []).find(
              (p: any) => String(p.type) === String(benefitType)
            );
            if (!plan) return null;
            return {
              planId: plan.id,
              waiverReason: waiverReasonByType[benefitType] || null,
            };
          })
          .filter(Boolean) as Array<{ planId: number; waiverReason?: string }>;

        // Get selected plan IDs for form associations
        const selectedPlanIds = Object.values(selectedModifyPlans)
          .map((sel: any) => sel?.planId)
          .filter((id): id is number => !!id);

        console.log(`[Benefits] Completed enrollment, selected plans:`, selectedPlanIds, selectedModifyPlans);

        const planById = (resolvedPlanSource || []).reduce(
          (acc: Record<number, any>, plan: any) => {
            if (plan?.id) acc[Number(plan.id)] = plan;
            return acc;
          },
          {}
        );

        // Set form review context and show modal
        console.log(`[Benefits] Setting form review data with ${selectedPlanIds.length} plans`);
        setFormReviewData({
          signatureName: localSignatureName,
          signatureData: localSignatureData,
          selectedPlanIds,
          enrollmentContext: {
            employeeId,
            clientId,
            planById,
            enrollmentState,
            selectedModifyPlans,
            waiversList,
            waivedCoverageByType,
            waiverReasonByType,
            // Legacy wire format expected by the save handler — one string per
            // dep, flattened from the two parallel maps the UI owns.
            waiveReasons: Object.fromEntries(
              Object.keys(depReasonByType).map((type) => [
                type,
                flattenDepReasons(depReasonByType[type], depOtherTextByType[type]) ?? {},
              ]),
            ),
            qleEffectiveDate,
            qleEventDate,
            hasCompletedOpenEnrollment,
            oeProgressKey,
            dependents,
          },
        });
        setShowFormReview(true);
      } catch (error) {
        toast({
          title: "Error",
          description: error instanceof Error && error.message
            ? error.message
            : "Failed to prepare form review. Please try again.",
          variant: "destructive",
        });
      } finally {
        setIsSaving(false);
      }
    };

    return (
      <>
        <div className="max-w-4xl mx-auto p-6 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Edit className="w-5 h-5" />
                Digital Signature Required
              </CardTitle>
              <p className="text-gray-600">
                Complete your enrollment by providing your digital signature
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Legal Acknowledgment */}
              <div className="p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200">
                <h3 className="font-medium mb-2">Enrollment Acknowledgment</h3>
                <div className="text-sm text-gray-700 dark:text-gray-300 space-y-2">
                  <p>By signing below, I acknowledge that:</p>
                  <ul className="list-disc ml-6 space-y-1">
                    <li>I have reviewed and understand my benefit selections</li>
                    <li>The information provided is accurate and complete</li>
                    <li>I understand the costs and coverage details</li>
                    <li>Changes take effect as of the effective date of the plan</li>
                  </ul>
                </div>
              </div>

              {/* Name Input */}
              <div className="space-y-2">
                <Label htmlFor="signature-name">Full Legal Name</Label>
                <Input
                  id="signature-name"
                  type="text"
                  placeholder={user?.firstName + " " + user?.lastName}
                  value={localSignatureName}
                  onChange={(e) => setLocalSignatureName(e.target.value)}
                />
              </div>

              {/* Signature Canvas */}
              <div className="space-y-2">
                <Label>Digital Signature</Label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                  <SignatureCanvas
                    ref={localSignatureRef}
                    canvasProps={{
                      width: 600,
                      height: 200,
                      className: "border border-gray-300 dark:border-gray-600 rounded cursor-crosshair bg-white",
                    }}
                    onEnd={handleSignatureEnd}
                  />
                  <div className="flex justify-between items-center mt-2">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Sign above using your mouse or touchscreen
                    </p>
                    <Button variant="outline" size="sm" onClick={clearSignature}>
                      Clear Signature
                    </Button>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-between pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowSignaturePage(false);
                    setShowSummaryPage(true);
                  }}
                >
                  Back to Summary
                </Button>
                <Button
                  className="bg-green-600 hover:bg-green-700"
                  onClick={completeEnrollment}
                  disabled={isSaving}
                >
                  {isSaving ? "Processing..." : "Complete Enrollment"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

      </>
    );
  };

  // ONB-7: avoid flashing the unguarded UI while the redirect effect runs.
  if (employeeRowLoading || dobMissing) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Show appropriate page based on state
  if (showSignaturePage) {
    // The signing modal is rendered here — NOT inside SignaturePageComponent.
    // That component is defined inline in this render function, so its
    // identity changes every Benefits re-render and React remounts its whole
    // subtree. With the modal inside it, each draft-autosave PUT response
    // (setDraft → re-render → remount → re-seed signedKeys → autosave) looped
    // a PUT every ~1.2s and made the modal flash. Hoisted here, the modal's
    // parent (Benefits) is stable, so it never remounts.
    return (
      <>
        <SignaturePageComponent />
        {formReviewData && employeeRow && (
          <EnrollmentSigningView
            open={showFormReview}
            onOpenChange={setShowFormReview}
            signatureName={formReviewData.signatureName}
            signatureData={formReviewData.signatureData}
            selectedPlanIds={formReviewData.selectedPlanIds}
            employee={employeeRow}
            enrollmentContext={formReviewData.enrollmentContext}
            onConfirm={submitEnrollment}
            onBack={() => {
              setShowFormReview(false);
              setShowSignaturePage(false);
              setShowSummaryPage(true);
            }}
            isLoading={isSaving}
            initialSignedKeys={enrollmentDraft?.signedKeys ?? []}
            onSignedKeysChange={(keys) =>
              saveEnrollmentDraft({ signedKeys: keys })
            }
          />
        )}
      </>
    );
  }

  if (showSummaryPage) {
    return <SummaryPage />;
  }

  if (isInitialLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Benefits Enrollment</h1>
          <p className="text-muted-foreground">
            {canMakeChanges
              ? "Select your benefit plans and coverage levels"
              : "View your current benefits and enrollment status"}
          </p>
        </div>
        <Badge variant="secondary" className="text-sm">
          Employee Portal
        </Badge>
      </div>

      {/* Enrollment status banner */}
      <Card className="relative border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950">
        <div className="pointer-events-none absolute left-0 top-0 h-full w-1.5 bg-blue-500 rounded-l-md" />
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2">
            <Info className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            {enrollmentState === "open_enrollment"
              ? "Open Enrollment is active"
              : enrollmentState === "new_hire"
              ? "New Hire Benefits Enrollment"
              : enrollmentState === "qualifying_event"
              ? "You can make changes to your benefits"
              : "Benefits change window is closed"}
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="text-sm text-muted-foreground">
            {enrollmentState === "open_enrollment" ? (
              <div>
                <div>
                  Open Enrollment Window:{" "}
                  {oeWindow?.startDate
                    ? formatDateDisplay(oeWindow.startDate)
                    : "TBD"}{" "}
                  -
                  {oeWindow?.endDate
                    ? formatDateDisplay(oeWindow.endDate)
                    : "TBD"}
                </div>
              </div>
            ) : enrollmentState === "new_hire" ? (
              <div>
                {resolvedPlanSource.length === 0 ? (
                  <>No plans available yet. Contact your administrator.</>
                ) : (
                  <>
                    You have{" "}
                    {newHireWindow?.daysRemaining === 0
                      ? "until today"
                      : newHireWindow?.daysRemaining === 1
                      ? "1 day"
                      : `${newHireWindow?.daysRemaining || 0} days`}{" "}
                    to enroll in benefits.
                    {newHireWindow?.windowEndDate && (
                      <> Window closes {formatDateDisplay(newHireWindow.windowEndDate)}.</>
                    )}
                  </>
                )}
              </div>
            ) : (
              <div>
                <div>
                  Changes are allowed only during open enrollment or due to a
                  qualifying life event.
                </div>
                {qualifyingEvents?.activeEvent ? (
                  <div className="mt-1 text-blue-800 dark:text-blue-300">
                    Eligible due to qualifying event:{" "}
                    {String(qualifyingEvents.activeEvent.eventType || "N/A")}
                    {qualifyingEvents.activeEvent.eventDate ? (
                      <>
                        {" "}
                        on{" "}
                        {formatDateDisplay(qualifyingEvents.activeEvent.eventDate)}
                      </>
                    ) : null}
                    {qualifyingEvents?.eventDeadline ? (
                      <>
                        {" "}
                        — change window ends{" "}
                        {formatDateDisplay(qualifyingEvents.eventDeadline)}
                      </>
                    ) : null}
                    {qleEventDate && (
                      <div className="mt-1 font-medium">
                        Qualifying event date: {qleEventDate.toLocaleDateString()}
                      </div>
                    )}
                  </div>
                ) : (
                  qualifyingEvents?.availableEvents && (
                    <div className="mt-1">
                      <div className="text-xs">Qualifying events include:</div>
                      <ul className="list-disc pl-5 text-xs">
                        {qualifyingEvents.availableEvents.map((e: any) => (
                          <li key={String(e)}>{String(e)}</li>
                        ))}
                      </ul>
                      {Array.isArray(qualifyingEvents?.recentEvents) &&
                        qualifyingEvents.recentEvents.length > 0 && (
                          <div className="mt-3">
                            <div className="text-xs font-medium text-muted-foreground mb-1.5">
                              Your recent qualifying events
                            </div>
                            <div className="space-y-1.5">
                              {qualifyingEvents.recentEvents
                                .slice(0, 5)
                                .map((ev: any) => {
                                  const status = String(
                                    ev.status || ""
                                  ).toLowerCase();
                                  const StatusIcon =
                                    status === "approved"
                                      ? CheckCircle
                                      : status === "denied" ||
                                          status === "rejected"
                                        ? AlertCircle
                                        : Clock;
                                  const statusStyle =
                                    status === "approved"
                                      ? "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300"
                                      : status === "denied" ||
                                          status === "rejected"
                                        ? "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300"
                                        : "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300";
                                  return (
                                    <div
                                      key={ev.id}
                                      className="flex items-center justify-between gap-2 rounded-md border bg-card px-2.5 py-1.5"
                                    >
                                      <div className="flex items-center gap-2 min-w-0 text-xs">
                                        <Calendar className="h-4 w-4 shrink-0 text-muted-foreground" />
                                        <span className="font-medium capitalize truncate text-foreground">
                                          {String(ev.eventType).replace(
                                            /_/g,
                                            " "
                                          )}
                                        </span>
                                        <span className="text-muted-foreground shrink-0">
                                          {formatDateDisplay(ev.eventDate)}
                                        </span>
                                      </div>
                                      <span
                                        className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium capitalize shrink-0 ${statusStyle}`}
                                      >
                                        <StatusIcon className="h-3 w-3" />
                                        {status || "pending"}
                                      </span>
                                    </div>
                                  );
                                })}
                            </div>
                          </div>
                        )}
                    </div>
                  )
                )}
              </div>
            )}
          </div>
          <div className="flex gap-2">
            {canMakeChanges ? (
              <Button
                className="bg-blue-600 hover:bg-blue-700"
                disabled={enrollmentState === "new_hire" && resolvedPlanSource.length === 0}
                onClick={async () => {
                  // Only mark OE progress as "pending" on FIRST enrollment, not modifications
                  if (!hasCompletedOpenEnrollment && (oeProgressKey || oeWindow?.startDate) && user?.clientId && employeeRow?.id) {
                    try {
                      await fetch(`/api/clients/${user.clientId}/open-enrollment/progress`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({
                          employeeId: employeeRow.id,
                          startDate: oeProgressKey || oeWindow?.startDate,
                          status: "pending",
                        }),
                      });
                    } catch (e) {
                      console.error(e)
                    }
                  }

                  // Pre-populate selections from enrollments when modifying
                  // During OE: prefer upcoming OE enrollments (matching OE effective date) over currently-active ones
                  if ((hasCompletedOpenEnrollment || hasExistingEnrollments) && Array.isArray(currentEnrollments)) {
                    const initialSelections: Record<string, { planId: number; dependentIds: number[] }> = {};
                    const initialWaivers: Record<string, boolean> = {};
                    const initialWaiverReasons: Record<string, string> = {};
                    const initialWaiveReasons: { [key: string]: { [dependentId: string]: string } } = {};

                    // Only pre-select a past election if its plan is still
                    // selectable now — mirrors the plan-card filter. Prevents a
                    // hidden/un-electable plan (e.g. an FSA that lost its setup,
                    // or a plan no longer offered) from being silently selected
                    // and dead-ending the submit.
                    const isSelectable = (planId: number): boolean => {
                      const p = (resolvedPlanSource as any[]).find((x: any) => x.id === planId);
                      if (!p) return false;
                      if (isFsaPlanType(String(p.type)) && !isFsaConfigured(fsaConfigByPlanId.get(planId))) return false;
                      return true;
                    };

                    // During OE: first pass — pick upcoming OE enrollments matching the OE effective date
                    const oeEffDateStr = oeProgressKey ? String(oeProgressKey).split('T')[0] : null;
                    if (isOpenEnrollmentActive && oeEffDateStr) {
                      for (const e of (currentEnrollments as any[])) {
                        const type = String(e.planType || "").toLowerCase();
                        if (!type) continue;
                        const src = String(e.enrollmentSource || "").toLowerCase();
                        if (src !== "open_enrollment") continue;
                        if (!e.effectiveDate || String(e.effectiveDate).split('T')[0] !== oeEffDateStr) continue;
                        if (e.waivedCoverage || String(e.status || "").toLowerCase() === "waived") {
                          initialWaivers[type] = true;
                          if (e.waiverReason) initialWaiverReasons[type] = e.waiverReason;
                        } else if (isActiveEnrollment(e) || String(e.status || "").toLowerCase() === "pending") {
                          if (!isSelectable(e.planId)) continue;
                          initialSelections[type] = {
                            planId: e.planId,
                            dependentIds: e.enrolledDependents || [],
                          };
                          if (e.dependentWaiverReasons && typeof e.dependentWaiverReasons === 'object') {
                            initialWaiveReasons[type] = e.dependentWaiverReasons as Record<string, string>;
                          }
                        }
                      }
                    }

                    // Second pass: fill remaining types from any active/pending enrollment (fallback)
                    for (const e of (currentEnrollments as any[])) {
                      const type = String(e.planType || "").toLowerCase();
                      if (!type) continue;
                      // Skip if already filled from OE enrollments above
                      if (initialSelections[type] || initialWaivers[type]) continue;
                      if (e.waivedCoverage || String(e.status || "").toLowerCase() === "waived") {
                        initialWaivers[type] = true;
                        if (e.waiverReason) {
                          initialWaiverReasons[type] = e.waiverReason;
                        }
                      } else if (isActiveEnrollment(e) || String(e.status || "").toLowerCase() === "pending") {
                        if (!isSelectable(e.planId)) continue;
                        initialSelections[type] = {
                          planId: e.planId,
                          dependentIds: e.enrolledDependents || [],
                        };
                        if (e.dependentWaiverReasons && typeof e.dependentWaiverReasons === 'object') {
                          initialWaiveReasons[type] = e.dependentWaiverReasons as Record<string, string>;
                        }
                      }
                    }
                    setSelectedModifyPlans(initialSelections);
                    setWaivedCoverageByType(initialWaivers);
                    setWaiverReasonByType(initialWaiverReasons);
                    // Classify each saved dep waiver into the two-map shape.
                    const initialDepReasons: Record<string, Record<number, string>> = {};
                    const initialDepOtherTexts: Record<string, Record<number, string>> = {};
                    for (const [type, perDep] of Object.entries(initialWaiveReasons)) {
                      initialDepReasons[type] = {};
                      initialDepOtherTexts[type] = {};
                      for (const [depId, stored] of Object.entries(perDep)) {
                        const cls = classifyDepReason(stored);
                        if (cls.reason) initialDepReasons[type][Number(depId)] = cls.reason;
                        if (cls.otherText) initialDepOtherTexts[type][Number(depId)] = cls.otherText;
                      }
                    }
                    setDepReasonByType(initialDepReasons);
                    setDepOtherTextByType(initialDepOtherTexts);
                  }

                  // Auto-select mandatory plans (runs for ALL enrollment flows, including new hires)
                  {
                    const mandatorySelections: Record<string, { planId: number; dependentIds: number[] }> = {};
                    const mandatoryWaiverClears: string[] = [];
                    for (const p of (resolvedPlanSource || []) as any[]) {
                      if (!Boolean(p.enrollmentRequired)) continue;
                      const t = String(p.type).toLowerCase();
                      mandatorySelections[t] = { planId: p.id, dependentIds: [] };
                      mandatoryWaiverClears.push(t);
                    }
                    if (Object.keys(mandatorySelections).length > 0) {
                      setSelectedModifyPlans((prev) => ({ ...prev, ...mandatorySelections }));
                      setWaivedCoverageByType((prev) => {
                        const next = { ...prev };
                        for (const t of mandatoryWaiverClears) delete next[t];
                        return next;
                      });
                    }
                  }

                  setShowModifyBenefitsModal(true);
                  setModifyStep(0);
                }}
              >
                {enrollmentState === "open_enrollment"
                  ? (hasCompletedOpenEnrollment ? "Modify Elections" : "Begin Open Enrollment")
                  : hasExistingEnrollments ? "Modify Enrollment" : "Begin Enrollment"}
              </Button>
            ) : (
              <Button
                variant="outline"
                onClick={() => setShowQualifyingEventForm(true)}
              >
                Report Qualifying Event
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* QLE submitted, awaiting broker/employer approval (change window not yet open) */}
      {qlePendingReview && !qleSubmittedPending && !canMakeChanges && (
        <Card className="relative border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950">
          <div className="pointer-events-none absolute left-0 top-0 h-full w-1.5 bg-amber-500 rounded-l-md" />
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5" />
              <div>
                <p className="font-medium text-amber-900 dark:text-amber-100">
                  Qualifying event pending approval
                </p>
                <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                  Your qualifying life event has been submitted and is being reviewed by your broker. You'll be able to make benefit changes once it's approved.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* QLE / Enrollment denied */}
      {qleDenied && !qleSubmittedPending && (
        <Card className="border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-700 dark:text-red-400 mt-0.5" />
              <div>
                <p className="font-medium text-red-900 dark:text-red-100">
                  Enrollment request denied
                </p>
                <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                  Your recent benefit change request was not approved. Contact your broker for details or to submit a new qualifying event.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Enrollment pending broker approval — show elected plans */}
      {(() => {
        // Find the still-pending enrollment request ticket with plan details
        const enrollTicket = (myPendingTickets as any[]).find(isPendingEnrollmentRequestTask);
        const cf = (enrollTicket as any)?.customFields || {};
        const elections: any[] = Array.isArray(cf.requestedEnrollments) ? cf.requestedEnrollments : [];
        const waivers: any[] = Array.isArray(cf.requestedWaivers) ? cf.requestedWaivers : [];
        const effDate = cf.effectiveDate;

        if (!qleSubmittedPending) return null;
        return (
          <PendingEnrollmentRequestCard
            elections={elections}
            waivers={waivers}
            effectiveDate={effDate}
            plans={employeeAllPlans as any[]}
          />
        );
      })()}

      {/* Report Qualifying Life Event Modal (always available when triggered) */}
      <Dialog
        open={showQualifyingEventForm}
        onOpenChange={setShowQualifyingEventForm}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Report Qualifying Life Event
            </DialogTitle>
            <DialogDescription>
              Submit your event and optionally pick a plan to start a pending
              enrollment.
            </DialogDescription>
          </DialogHeader>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmitQualifyingEvent)}
              className="space-y-4"
            >
              <FormField
                control={form.control}
                name="eventType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Event Type</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a qualifying life event" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="marriage">Marriage</SelectItem>
                        <SelectItem value="birth">
                          Birth/Adoption of Child
                        </SelectItem>
                        <SelectItem value="divorce">
                          Divorce/Legal Separation
                        </SelectItem>
                        <SelectItem value="death">
                          Death of Spouse/Dependent
                        </SelectItem>
                        <SelectItem value="loss_coverage">
                          Loss of Other Coverage
                        </SelectItem>
                        <SelectItem value="employment_change">
                          Change in Employment Status
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="eventDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Event Date</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {qleAllowsDependents && (
                <div className="space-y-3 rounded-md border p-4">
                  <div>
                    <h4 className="text-sm font-medium">
                      Add a dependent (optional)
                    </h4>
                    <p className="text-xs text-muted-foreground">
                      Add your new {qleEventType === "marriage" ? "spouse" : "child"} here
                      so you can enroll them right after submitting. It's saved
                      automatically when you click Submit Event.
                    </p>
                  </div>
                  <DependentQuickAddForm
                    ref={qleDependentFormRef}
                    hideButtons
                    defaultRelationship={qleEventType === "marriage" ? "spouse" : "child"}
                  />
                </div>
              )}
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Please describe the qualifying life event in detail..."
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="space-y-2">
                <label className="text-sm font-medium">Supporting Documentation (Optional)</label>
                <Input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  onChange={(e) => {
                    const file = e.target.files?.[0] || null;
                    setQleFile(file);
                    form.setValue("documentation", file?.name || "");
                  }}
                />
                {qleFile && (
                  <p className="text-xs text-muted-foreground">{qleFile.name} ({(qleFile.size / 1024).toFixed(0)} KB)</p>
                )}
              </div>

              {/* Removed: optional start an enrollment section per requirements */}

              <div className="flex justify-end space-x-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    // setQleAddedDependents was removed when dependent capture moved to
                    // qleDependentFormRef (DependentQuickAddForm ref) — call left orphaned,
                    // crashed onClick in prod (ReferenceError). Dialog unmounts the form on
                    // close, so its internal draft state already resets; nothing to add here.
                    setShowQualifyingEventForm(false);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={qualifyingEventMutation.isPending || qleSubmitting}
                >
                  {qualifyingEventMutation.isPending || qleSubmitting
                    ? "Submitting..."
                    : "Submit Event"}
                </Button>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* My Current Benefits — v2 type-sectioned layout (read-only). */}
      {(() => {
        if (enrollmentsLoading) {
          return (
            <Card className="border-l-4 border-l-purple-500">
              <CardContent className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </CardContent>
            </Card>
          );
        }

        const { activeBenefits, pendingBenefits, waivedBenefits, employeeBenefitsForRows, totalMonthly } = v2CurrentBenefits;
        const typeSections = v2TypeSections;
        const hasAnyRow =
          activeBenefits.length > 0 || pendingBenefits.length > 0 || waivedBenefits.length > 0;

        return (
          <div className="space-y-6">
            {/* Coverage banner (progress-only on the employee view, no CTA) */}
            <CoverageHeaderBanner
              totalTypes={0}
              configuredTypes={0}
              segments={typeSections.segmentCounts}
              totalMonthly={totalMonthly}
              payCycle={employeeRow?.payCycle}
            />

            {!hasAnyRow && (
              <Card className="border-l-4 border-l-purple-500">
                <CardContent className="flex flex-col items-center justify-center py-8">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                    No active benefit enrollments.
                  </p>
                  {withinOpenEnrollment && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Select plans during open enrollment to enroll in benefits.
                    </p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Upcoming Benefits — kept as its own top-level section. */}
            {pendingBenefits.length > 0 && (
              <Card className="relative border-blue-200 dark:border-blue-800 bg-blue-50/40 dark:bg-blue-950/30">
                <div className="pointer-events-none absolute left-0 top-0 h-full w-1.5 bg-blue-500 rounded-l-md" />
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    Upcoming Benefits
                  </CardTitle>
                  <CardDescription className="text-xs text-muted-foreground">
                    Benefits that will take effect on a future date.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {pendingBenefits.map((b: any) => (
                      <ActiveBenefitRow
                        key={b.id}
                        benefit={b}
                        employee={employeeRow}
                        dependents={dependents as any[]}
                        employeeBenefits={employeeBenefitsForRows}
                        onModify={() => { /* employee can't modify here; handled by OE / QLE flow above */ }}
                        readOnly
                        viewerIsEmployee
                        allowBreakdown={allowBreakdown}
                      />
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Type-sectioned current elections */}
            {hasAnyRow && (
              <div className="space-y-5">
                {typeSections.sections
                  .filter((s) => s.activeRows.length > 0 || s.waivedRows.length > 0)
                  .map((section) => {
                    const hasRowsHere =
                      section.activeRows.length > 0 || section.waivedRows.length > 0;
                    const defaultOpen = !section.def.collapsible || hasRowsHere;
                    return (
                      <BenefitSection
                        key={section.def.id}
                        def={section.def}
                        counts={{
                          enrolled: section.activeRows.length,
                          pending: section.pendingCount,
                          waived: section.waivedRows.length,
                          unconfigured: 0,
                        }}
                        defaultOpen={defaultOpen}
                        canConfigure={false}
                      >
                        {section.activeRows.map((b: any) => (
                          <ActiveBenefitRow
                            key={b.id}
                            benefit={b}
                            employee={employeeRow}
                            dependents={dependents as any[]}
                            employeeBenefits={employeeBenefitsForRows}
                            onModify={() => { /* employee can't modify here; OE / QLE flow above handles it */ }}
                            readOnly
                            viewerIsEmployee
                            allowBreakdown={allowBreakdown}
                          />
                        ))}
                        {section.waivedRows.map((b: any) => (
                          <WaivedBenefitRow
                            key={b.id}
                            benefit={b}
                            onEditWaiver={() => { /* read-only on employee view */ }}
                            onEnrollInstead={() => { /* read-only on employee view */ }}
                            readOnly
                          />
                        ))}
                      </BenefitSection>
                    );
                  })}
              </div>
            )}
          </div>
        );
      })()}


      {/* Payroll Deduction Summary - Show always */}
      <Card className="relative border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950">
        <div className="pointer-events-none absolute left-0 top-0 h-full w-1.5 bg-green-500 rounded-l-md" />
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-green-600 dark:text-green-400" />
            {hasNonMonthlyPayCycle(employeeRow?.payCycle)
              ? `${getPayCycleLabelCapitalized(employeeRow?.payCycle)} Deduction Summary`
              : "Monthly Cost Summary"}
          </CardTitle>
          <CardDescription className="text-xs text-muted-foreground">
            Your current {getPayCycleLabel(employeeRow?.payCycle)} deductions from payroll for active benefit enrollments.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {(() => {
            const activeEnrollments = (currentEnrollments as any[]).filter(isCurrentlyEffectiveEnrollment);
            return (
              <DeductionSummaryContent
                items={activeEnrollments.map((e: any) => ({
                  label: e.planName,
                  // This page's rows rename employeePremium → monthlyPremium.
                  monthlyAmount: enrollmentEmployeeMonthly({
                    employeePremium: e.monthlyPremium,
                    totalPremium: e.totalPremium,
                    planType: e.planType,
                  }),
                }))}
                payCycle={employeeRow?.payCycle}
                showTaxSavings
                emptyMessage={
                  <div className="text-sm text-muted-foreground">
                    <div className="mt-2 italic">No active benefit deductions.</div>
                    <div className="mt-1 text-xs">
                      Your monthly cost summary will appear here once you have active benefit enrollments.
                    </div>
                  </div>
                }
              />
            );
          })()}
        </CardContent>
      </Card>









      {/* Beneficiaries Modal */}
      <Dialog
        open={showBeneficiariesModal}
        onOpenChange={setShowBeneficiariesModal}
      >
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Life Insurance Beneficiaries</DialogTitle>
            <DialogDescription>
              Manage your life insurance beneficiaries and allocation
              percentages. Total allocation must equal 100%.
            </DialogDescription>
          </DialogHeader>

          <BeneficiariesManager
            planId={currentPlanId}
            beneficiaries={beneficiaries}
            onClose={() => setShowBeneficiariesModal(false)}
            onUpdate={refetchBeneficiaries}
          />
        </DialogContent>
      </Dialog>

      {/* Modify Benefits Modal - Step-by-Step Flow */}
      <Dialog
        open={showModifyBenefitsModal}
        onOpenChange={(open) => {
          setShowModifyBenefitsModal(open);
          if (!open) {
            // Reset step when closing
            setModifyStep(0);
            setUnlockedModifySteps(new Set());
          }
        }}
      >
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          {availableBenefitTypes.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-muted-foreground mb-4">
                No benefit plans are available for enrollment at this time.
              </p>
              <Button onClick={() => setShowModifyBenefitsModal(false)}>
                Close
              </Button>
            </div>
          ) : (
            <>
          <DialogHeader>
            <DialogTitle>
              {(() => {
                const stepNames: Record<string, string> = {
                  health: "Medical Insurance",
                  dental: "Dental Insurance",
                  vision: "Vision Insurance",
                  life: "Life Insurance",
                  disability: "Disability Insurance",
                };
                const currentType = availableBenefitTypes[modifyStep] || "";
                const stepName = stepNames[currentType] || `${getPlanTypeMeta(currentType).label} Insurance`;
                return `Modify ${stepName} - Step ${
                  modifyStep + 1
                } of ${availableBenefitTypes.length}`;
              })()}
            </DialogTitle>
            <DialogDescription>
              {(() => {
                const descriptions: Record<string, string> = {
                  health: "Select your medical insurance plan and manage dependent coverage",
                  dental: "Choose your dental insurance plan and coverage options",
                  vision: "Select your vision insurance plan for eye care coverage",
                  life: "Configure your life insurance coverage and beneficiaries",
                  disability: "Set up your disability insurance protection",
                };
                const currentType = availableBenefitTypes[modifyStep] || "";
                return descriptions[currentType] || "Select your plan and coverage options";
              })()}
            </DialogDescription>
          </DialogHeader>

          
          {/* Progress Indicator */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium">Progress</span>
              <span className="text-sm text-muted-foreground">
                {modifyStep + 1} of {availableBenefitTypes.length}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${((modifyStep + 1) / availableBenefitTypes.length) * 100}%` }}
              ></div>
            </div>
            <div className="flex justify-between mt-2 text-xs text-muted-foreground">
              {availableBenefitTypes.map((type) => {
                const labels: Record<string, string> = {
                  health: "Medical",
                  dental: "Dental",
                  vision: "Vision",
                  life: "Life",
                  disability: "Disability",
                };
                return <span key={type}>{labels[type] || getPlanTypeMeta(type).label}</span>;
              })}
            </div>
          </div>

          <div className="space-y-6">
            {/* Plan Selection & Comparison */}
           

              {/* Current enrollment indicator */}
              {(() => {
                const currentType = availableBenefitTypes[modifyStep] || "";
                const currentEnrollment = (currentEnrollments as any[])?.find(
                  (e: any) => e.planType === currentType && isActiveEnrollment(e)
                );
                const isCurrentlyWaived = (currentEnrollments as any[])?.find(
                  (e: any) =>
                    e.planType === currentType &&
                    (Boolean(e.waivedCoverage) || String(e.status || "").toLowerCase() === "waived")
                );

                if (currentEnrollment) {
                  const depIds = currentEnrollment.enrolledDependents || [];
                  const coveredDeps = (dependents as any[]).filter((d: any) => depIds.includes(d.id));
                  const formatTierLabel = (tier: string) =>
                    tier
                      .replace("employee_only", "Employee Only")
                      .replace("employee_spouse", "Employee + Spouse")
                      .replace("employee_children", "Employee + Children")
                      .replace("family", "Family")
                      .replace(/_/g, " ");
                  return (
                    <div className="w-full p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-900">
                      <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0">
                          <span className="text-xs font-medium uppercase tracking-wide text-blue-600 dark:text-blue-400">Current Plan</span>
                          <p className="font-semibold text-sm mt-1">{currentEnrollment.planName}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            {formatTierLabel(currentEnrollment.coverageLevel || "employee_only")}
                          </p>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-lg font-bold text-blue-700 dark:text-blue-300">
                            ${hasNonMonthlyPayCycle(employeeRow?.payCycle)
                              ? calculatePerPaycheckAmount(Number(currentEnrollment.monthlyPremium || 0), employeeRow?.payCycle).toFixed(2)
                              : Number(currentEnrollment.monthlyPremium || 0).toFixed(2)}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {hasNonMonthlyPayCycle(employeeRow?.payCycle)
                              ? `per ${getPayCycleLabel(employeeRow?.payCycle)}`
                              : "per month"}
                          </p>
                          {hasNonMonthlyPayCycle(employeeRow?.payCycle) && (
                            <p className="text-xs text-gray-400 dark:text-gray-500">
                              (${Number(currentEnrollment.monthlyPremium || 0).toFixed(2)}/mo)
                            </p>
                          )}
                        </div>
                      </div>
                      {coveredDeps.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-blue-200 dark:border-blue-800">
                          <p className="text-xs font-medium text-gray-500 mb-1">Covered Dependents</p>
                          <div className="space-y-0.5">
                            {coveredDeps.map((d: any) => (
                              <p key={d.id} className="text-xs text-gray-600 dark:text-gray-400">
                                {d.firstName} {d.lastName}
                              </p>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                } else if (isCurrentlyWaived) {
                  return (
                    <div className="inline-flex text-sm px-3 py-1.5 bg-orange-50 dark:bg-orange-950/30 rounded-full border border-orange-200 dark:border-orange-900">
                      <span className="text-orange-700 dark:text-orange-300">Current:</span>&nbsp;
                      <span className="font-medium">Coverage waived</span>
                    </div>
                  );
                }
                return (
                  <div className="inline-flex text-sm px-3 py-1.5 bg-gray-50 dark:bg-gray-900/50 rounded-full border border-gray-200 dark:border-gray-800">
                    <span className="text-muted-foreground">Current:</span>&nbsp;
                    <span className="font-medium">Not enrolled</span>
                  </div>
                );
              })()}

              {isLockedStep && (
                <div className="p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-800">
                  <div className="flex items-center gap-2 mb-2">
                    <Shield className="w-5 h-5 text-gray-500" />
                    <h4 className="font-medium text-gray-700 dark:text-gray-300">Coverage Already in Effect</h4>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    This plan's coverage is already in effect and cannot be modified during this enrollment window.
                  </p>
                </div>
              )}

              {!isLockedStep && (
 <div className="space-y-4">
              {isReadOnlyStep && (
                <div className="flex items-center justify-between p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <div className="flex items-center gap-2">
                    <Eye className="w-5 h-5 text-blue-600" />
                    <div>
                      <p className="font-medium text-blue-900 dark:text-blue-100">Your Current Elections</p>
                      <p className="text-sm text-blue-700 dark:text-blue-300">
                        Review your selections below. Click "Modify" to make changes.
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    className="border-blue-300 text-blue-700 hover:bg-blue-100"
                    onClick={() => setUnlockedModifySteps(prev => { const next = new Set(prev); next.add(modifyStep); return next; })}
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Modify
                  </Button>
                </div>
              )}
              <SectionHeader
                index={1}
                title={`${isReadOnlyStep ? "Review" : "Choose"} ${(({
                  health: "medical", dental: "dental", vision: "vision", life: "life", disability: "disability"
                } as Record<string, string>)[availableBenefitTypes[modifyStep] || ""] || getPlanTypeMeta(availableBenefitTypes[modifyStep] || "").label)} plan or waive`}
              />
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <h4 className="font-medium sr-only">
                  {isReadOnlyStep ? "Review" : "Choose"}{" "}
                  {(() => {
                    const stepLabels: Record<string, string> = {
                      health: "Medical",
                      dental: "Dental",
                      vision: "Vision",
                      life: "Life",
                      disability: "Disability",
                    };
                    const currentType = availableBenefitTypes[modifyStep] || "";
                    return stepLabels[currentType] || getPlanTypeMeta(currentType).label;
                  })()}{" "}
                  Plan
                </h4>
                {(() => {
                  const currentType = availableBenefitTypes[modifyStep] || "";
                  const count = comparePlans[currentType]?.length || 0;
                  if (isReadOnlyStep) return null;
                  return (
                    <div className="flex items-center gap-2">
                      {count > 0 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => clearComparison(currentType)}
                        >
                          Clear ({count})
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={count < 2}
                        onClick={() => setCompareDialogType(currentType)}
                        title={count < 2 ? "Select 2 or more plans to compare" : "Compare selected plans"}
                      >
                        Compare{count > 0 ? ` (${count})` : ""}
                      </Button>
                    </div>
                  );
                })()}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {(() => {
                  const currentType = availableBenefitTypes[modifyStep] || "";
                  const isWaived = !!waivedCoverageByType[currentType];
                  const stepLabels: Record<string, string> = {
                    health: "Medical",
                    dental: "Dental",
                    vision: "Vision",
                    life: "Life",
                    disability: "Disability",
                  };

                  // Dashed "Waive coverage" option — appended after the plan cards
                  // (matches the modal pattern). Selecting it sets waivedCoverageByType
                  // and clears the plan selection; the reason picker renders below in
                  // section 02 via WaiveReasonPicker.
                  const waiveCard = (
                    <button
                      key="waive-coverage"
                      type="button"
                      disabled={isReadOnlyStep}
                      onClick={() => {
                        setWaivedCoverageByType((prev) => ({ ...prev, [currentType]: true }));
                        setSelectedModifyPlans((prev) => ({
                          ...prev,
                          [currentType]: { planId: 0, dependentIds: [] },
                        }));
                      }}
                      className={`col-span-1 md:col-span-2 lg:col-span-3 w-full text-left rounded-lg border-2 border-dashed px-4 py-3 transition-colors ${
                        isWaived
                          ? "border-stone-400 bg-stone-50 dark:bg-stone-900/40"
                          : "border-stone-300 hover:border-stone-400 hover:bg-stone-50/60 dark:hover:bg-stone-900/30"
                      } ${isReadOnlyStep ? "opacity-50 cursor-not-allowed" : ""}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-sm font-medium text-stone-800 dark:text-stone-200">
                            {isWaived ? "✓ Waiving coverage" : "── Waive coverage ──"}
                          </div>
                          <div className="text-[11.5px] text-stone-500 mt-0.5">
                            Decline {stepLabels[currentType] || getPlanTypeMeta(currentType).label} insurance
                          </div>
                        </div>
                        <div className="text-[11.5px] text-stone-500 font-mono">$0.00</div>
                      </div>
                    </button>
                  );

                  // Use real client plans (eligible for OE when available), filtered by current benefit type
                  const sourcePlans = resolvedPlanSource as any[];

                  // Get enrolled plan for this type (to mark it in UI)
                  // Pick the most recent enrollment (latest effectiveDate or id)
                  const matchingEnrollments = (currentEnrollments as any[])?.filter(
                    (e: any) => {
                      const s = String(e.status || "").toLowerCase();
                      return e.planType === currentType && (s === "active" || s === "pending") && !e.waivedCoverage;
                    }
                  ) || [];
                  // Sort by effectiveDate desc so the latest enrollment wins
                  const sorted = [...matchingEnrollments].sort((a: any, b: any) => {
                    const da = a.effectiveDate ? new Date(a.effectiveDate).getTime() : 0;
                    const db = b.effectiveDate ? new Date(b.effectiveDate).getTime() : 0;
                    return db - da;
                  });
                  const currentEnrollmentForType = sorted[0] || null;
                  const currentPlanId = currentEnrollmentForType?.planId;
                  const currentEnrollmentStatus = String(currentEnrollmentForType?.status || "").toLowerCase();
                  const isPending = currentEnrollmentStatus === "pending";

                  const planCards = (sourcePlans || [])
                    .filter((p: any) => String(p.type) === currentType)
                    // Hide FSA plans that aren't set up yet — employees can't enroll
                    // into them and shouldn't see admin-facing config gaps.
                    .filter((p: any) => !fsaNeedsSetup(p.type, fsaConfigByPlanId.get(p.id)))
                    .map((plan: any) => {
                      const isSelected =
                        selectedModifyPlans[currentType]?.planId === plan.id;
                      const isCurrentlyEnrolled = plan.id === currentPlanId;
                      
                      // Check if plan was previously waived (for visual indicator)
                      const previouslyWaivedEnrollment = (currentEnrollments as any[])?.find(
                        (e: any) => 
                          e.planType === currentType && 
                          e.planId === plan.id &&
                          (Boolean(e.waivedCoverage) || String(e.status || "").toLowerCase() === "waived")
                      );
                      const isPreviouslyWaived = Boolean(previouslyWaivedEnrollment);

                      // Plan eligibility check: the /api/employees/:id/eligibility endpoint
                      // pre-computes this per plan, including employment type, waiting period,
                      // hours, and eligibility group matching.
                      const elig = eligibilityByPlanId.get(plan.id);
                      const isPlanEligible = !elig || elig.isEligible !== false;
                      const ineligibilityReason = elig?.ineligibilityReason || null;

                      // Ineligible plan: render a non-selectable card with the reason,
                      // matching the Eligibility tab's diagnosis.
                      if (!isPlanEligible) {
                        return (
                          <div
                            key={plan.id}
                            className="relative border rounded-lg p-4 bg-red-50/50 dark:bg-red-950/20 border-red-200 dark:border-red-800 opacity-80 pointer-events-none"
                          >
                            <div className="flex items-start justify-between mb-2">
                              <div>
                                <div className="flex items-center gap-2 flex-wrap">
                                  <h5 className="font-medium text-red-800 dark:text-red-200">{plan.name}</h5>
                                </div>
                                <p className="text-sm text-muted-foreground capitalize">{plan.type}</p>
                              </div>
                              <Badge variant="destructive" className="text-xs">Ineligible</Badge>
                            </div>
                            {ineligibilityReason && (
                              <p className="text-xs text-red-600 dark:text-red-400 mt-1">{ineligibilityReason}</p>
                            )}
                          </div>
                        );
                      }

                      const isWaivedType = !!waivedCoverageByType[currentType];
                      const selectedDependents = isWaivedType
                        ? (currentEnrollmentForType?.enrolledDependents || [])
                        : (selectedModifyPlans[currentType]?.dependentIds || []);

                      // Calculate premium using real rates + contributions. Full result feeds the
                      // breakdown popover; `totalPremium` is the employee monthly share.
                      const planPremiumResult = computePremiumResultForSelection({
                        plan,
                        ratesByPlanId: ratesByPlanId as any,
                        contribsByPlanId: contribsByPlanId as any,
                        employee: employeeRow as any,
                        selectedDependentIds: selectedDependents,
                        allDependents: dependents as any[],
                        ldConfigByPlanId,
                      });
                      const totalPremium = planPremiumResult?.employeePremium ?? 0;
                      const priceState = planCardPriceState(planPremiumResult, visiblePlanPremiumsLoading || ldConfigsLoading);

                      // FSA: cost = employee's annual election (not rate-based).
                      const planIsFsa = isFsaPlanType(String(plan.type || ""));
                      const fsaCfg = fsaConfigByPlanId.get(plan.id) || null;
                      const fsaElection = selectedModifyPlans[currentType]?.annualElection || "";
                      // Prorate over the exact remaining paychecks (effective date → plan-year end).
                      let fsaProrationOptions: FsaPreviewOptions | undefined;
                      if (planIsFsa) {
                        const fsaSchedule = resolvePaySchedule((employeeRow as any)?.payGroupId);
                        const fsaPlanYearEnd = (plan as any).endDate ? new Date((plan as any).endDate) : null;
                        if (fsaSchedule && fsaPlanYearEnd) {
                          const fsaEffIso =
                            enrollmentState === "qualifying_event" && qleEventDate
                              ? (qleEventDate instanceof Date ? qleEventDate.toISOString() : String(qleEventDate))
                              : eligibilityByPlanId.get(plan.id)?.effectiveDate || null;
                          const fsaFrom = fsaEffIso ? new Date(fsaEffIso) : new Date();
                          const { remainingPeriods, isExact } = computeFsaProration({
                            annualElection: Number(fsaElection) || 0,
                            schedule: fsaSchedule,
                            from: fsaFrom,
                            to: fsaPlanYearEnd,
                          });
                          fsaProrationOptions = { remainingPeriods, estimated: !isExact };
                        }
                      }
                      const fsaPreview = planIsFsa
                        ? computeFsaPreview(fsaElection, fsaCfg, employeeRow?.payCycle, fsaProrationOptions)
                        : null;
                      const fsaNotConfigured = fsaNeedsSetup(plan.type, fsaCfg);
                      
                      // Get selected members for coverage level display
                      const selectedMembers = (dependents as any[]).filter(
                        (d: any) => selectedDependents.includes(d.id)
                      );
                      const spouseSelected = selectedMembers.some(
                        (d: any) => String(d.relationship).toLowerCase() === "spouse"
                      );
                      const childSelected = selectedMembers.filter((d: any) =>
                        String(d.relationship).toLowerCase().startsWith("child")
                      );

                      // Determine coverage level text
                      const spouseCount = spouseSelected ? 1 : 0;
                      const childrenCount = childSelected.length;

                      let coverageLevel = "Employee Only";
                      if (selectedDependents.length > 0) {
                        if (spouseCount > 0 && childrenCount > 0) {
                          coverageLevel = "Employee + Family";
                        } else if (spouseCount > 0) {
                          coverageLevel = "Employee + Spouse";
                        } else if (childrenCount > 0) {
                          coverageLevel = `Employee + ${childrenCount} Child${
                            childrenCount > 1 ? "ren" : ""
                          }`;
                        }
                      }

                      const isInCompare = comparePlans[currentType]?.includes(plan.id) || false;
                      return (
                        <div
                          key={plan.id}
                          className={`relative border rounded-lg p-4 transition-all ${
                            isReadOnlyStep
                              ? isSelected
                                ? "border-blue-500 bg-blue-50 dark:bg-blue-950/20 pointer-events-none"
                                : "border-gray-200 opacity-50 grayscale pointer-events-none"
                              : (() => {
                                  // If a mandatory plan is selected in this category, lock the UI
                                  const currentSel = selectedModifyPlans[currentType];
                                  const selPlan = currentSel ? (sourcePlans || []).find((sp: any) => sp.id === currentSel.planId) : null;
                                  const isMandatoryLocked = Boolean((selPlan as any)?.enrollmentRequired);
                                  if (isMandatoryLocked && isSelected) return "border-blue-500 bg-blue-50 dark:bg-blue-950/20 cursor-not-allowed";
                                  if (isMandatoryLocked && !isSelected) return "border-gray-200 opacity-50 cursor-not-allowed";
                                  return `cursor-pointer ${isSelected ? "border-blue-500 bg-blue-50 dark:bg-blue-950/20" : "border-gray-200 hover:border-gray-300"}`;
                                })()
                          }`}
                          onClick={isReadOnlyStep ? undefined : () => {
                            // Block switching away from a mandatory (auto-enrollment) plan
                            const currentSelected = selectedModifyPlans[currentType];
                            if (currentSelected) {
                              const selectedPlan = (sourcePlans || []).find((sp: any) => sp.id === currentSelected.planId);
                              if (selectedPlan?.enrollmentRequired && plan.id !== currentSelected.planId) return;
                            }
                            setWaivedCoverageByType((prev) => ({
                              ...prev,
                              [currentType]: false,
                            }));
                            setSelectedModifyPlans((prev) => {
                              // If this is the first interaction with this benefit type, initialize with currently enrolled dependents
                              if (!prev[currentType]) {
                                const currentEnrollmentForDeps = (currentEnrollments as any[])?.find(
                                  (e: any) =>
                                    e.planType === currentType &&
                                    !e.waivedCoverage &&
                                    String(e.status || "").toLowerCase() !== "waived"
                                );
                                const currentlyEnrolledIds = currentEnrollmentForDeps?.enrolledDependents || [];

                                return {
                                  ...prev,
                                  [currentType]: {
                                    planId: plan.id,
                                    dependentIds: [...currentlyEnrolledIds],
                                  },
                                };
                              }

                              // Preserve existing dependent selections
                              return {
                                ...prev,
                                [currentType]: {
                                  ...prev[currentType],
                                  planId: plan.id,
                                },
                              };
                            });
                          }}
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <div className="flex items-center gap-2 flex-wrap">
                                <h5 className="font-medium">{plan.name}</h5>
                                {isCurrentlyEnrolled && (
                                  <Badge variant="outline" className={`text-xs ${isPending ? "bg-blue-100 text-blue-800 border-blue-300" : "bg-green-100 text-green-800 border-green-300"}`}>
                                    {isPending
                                      ? `Pending — Starts ${currentEnrollmentForType?.effectiveDate ? formatDateDisplay(currentEnrollmentForType.effectiveDate) : ""}`
                                      : "Currently Enrolled"}
                                  </Badge>
                                )}
                                {isPreviouslyWaived && !isCurrentlyEnrolled && (
                                  <Badge variant="outline" className="text-xs bg-orange-100 text-orange-800 border-orange-300">
                                    Previously Waived
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm text-muted-foreground capitalize">
                                {plan.type}
                              </p>
                            </div>
                            <div className="text-right">
                              {planIsFsa ? (
                                <>
                                  <div className="text-lg font-bold text-green-600">
                                    ${fsaPreview!.perPaycheckEmployeeContribution.toFixed(2)}
                                  </div>
                                  <div className="text-xs text-muted-foreground">
                                    employee {fsaPreview!.perPaycheckLabel}
                                  </div>
                                  <div className="text-xs text-blue-600 dark:text-blue-400">
                                    Coverage: Employee Only
                                  </div>
                                </>
                              ) : (
                                <>
                                  {/* allowBreakdown = client's "Show employees how their premium is calculated" setting */}
                                  <div className="flex items-center justify-end gap-1.5">
                                    {priceState === "loading" ? (
                                      <Skeleton className="h-6 w-20" />
                                    ) : priceState === "unavailable" ? (
                                      <div className="text-sm font-medium text-amber-600 dark:text-amber-400">
                                        Price unavailable
                                      </div>
                                    ) : (
                                      <div className="text-lg font-bold text-green-600">
                                        ${hasNonMonthlyPayCycle(employeeRow?.payCycle)
                                          ? calculatePerPaycheckAmount(totalPremium, employeeRow?.payCycle).toFixed(2)
                                          : totalPremium.toFixed(2)}
                                      </div>
                                    )}
                                    {allowBreakdown && planPremiumResult && (
                                      <PremiumBreakdownPopover
                                        result={planPremiumResult}
                                        payCycle={employeeRow?.payCycle}
                                        planName={plan.name}
                                        coverageTier={coverageLevel}
                                        employee={employeeRow}
                                        coveredDependents={selectedMembers}
                                        isLoading={visiblePlanPremiumsLoading}
                                      />
                                    )}
                                  </div>
                                  {priceState === "ready" && (
                                    <div className="text-xs text-muted-foreground">
                                      employee per {getPayCycleLabel(employeeRow?.payCycle)}
                                    </div>
                                  )}
                                  {priceState === "ready" && hasNonMonthlyPayCycle(employeeRow?.payCycle) && (
                                    <div className="text-xs text-muted-foreground">
                                      (${totalPremium.toFixed(2)}/month)
                                    </div>
                                  )}
                                  <div className="text-xs text-blue-600 dark:text-blue-400">
                                    Coverage: {coverageLevel}
                                  </div>
                                </>
                              )}
                            </div>
                          </div>

                          <div className="space-y-2">
                            <div className="text-sm">
                              <div className="font-medium">Plan Details</div>
                              {(plan.deductible || plan.copay || plan.coinsurance || plan.maxOutOfPocket || plan.outOfPocketMax) ? (
                                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-muted-foreground mt-1">
                                  {plan.deductible && (
                                    <div><span className="font-medium text-foreground">Deductible:</span> {plan.deductible}</div>
                                  )}
                                  {plan.copay && (
                                    <div><span className="font-medium text-foreground">Copay:</span> {plan.copay}</div>
                                  )}
                                  {plan.coinsurance && (
                                    <div><span className="font-medium text-foreground">Coinsurance:</span> {plan.coinsurance}</div>
                                  )}
                                  {(plan.maxOutOfPocket || plan.outOfPocketMax) && (
                                    <div><span className="font-medium text-foreground">Max OOP:</span> {plan.maxOutOfPocket || plan.outOfPocketMax}</div>
                                  )}
                                  {plan.specialist && (
                                    <div><span className="font-medium text-foreground">Specialist:</span> {plan.specialist}</div>
                                  )}
                                  {plan.emergencyRoom && (
                                    <div><span className="font-medium text-foreground">ER:</span> {plan.emergencyRoom}</div>
                                  )}
                                  {plan.urgentCare && (
                                    <div><span className="font-medium text-foreground">Urgent Care:</span> {plan.urgentCare}</div>
                                  )}
                                  {plan.rxGeneric && (
                                    <div><span className="font-medium text-foreground">Rx Generic:</span> {plan.rxGeneric}</div>
                                  )}
                                </div>
                              ) : (
                                <div className="text-muted-foreground">
                                  {plan.description || "See SBC for full details"}
                                </div>
                              )}
                            </div>
                          </div>

                          {planIsFsa && isSelected && !fsaNotConfigured && !payScheduleReady && !payScheduleLoading && (
                            <div
                              className="mt-3 pt-3 border-t border-amber-200 dark:border-amber-800 flex items-start gap-2 text-[11px] text-amber-800 dark:text-amber-200"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                              <span>{FSA_NO_PAY_SCHEDULE_EMPLOYEE}</span>
                            </div>
                          )}

                          {planIsFsa && isSelected && !fsaNotConfigured && payScheduleReady && (
                            <div
                              className="mt-3 pt-3 border-t border-blue-200 dark:border-blue-800"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <FsaElectionInput
                                compact
                                value={fsaElection}
                                onChange={(v) =>
                                  setSelectedModifyPlans((prev) => ({
                                    ...prev,
                                    [currentType]: {
                                      ...(prev[currentType] || { planId: plan.id, dependentIds: [] }),
                                      annualElection: v,
                                    },
                                  }))
                                }
                                fsaConfig={fsaCfg}
                                payCycle={employeeRow?.payCycle}
                                prorationOptions={fsaProrationOptions}
                              />
                            </div>
                          )}

                          {(isSelected || (() => {
                            if (enrollmentState === "open_enrollment") return true;
                            if (enrollmentState === "qualifying_event") return !!qleEventDate;
                            return !!eligibilityByPlanId.get(plan.id)?.effectiveDate;
                          })()) && (
                            <div className="mt-3 pt-3 border-t border-blue-200 dark:border-blue-800 flex flex-wrap gap-2">
                              {isSelected && (
                                <Badge
                                  variant="secondary"
                                  className="bg-blue-100 text-blue-800"
                                >
                                  {plan.enrollmentRequired ? "Auto-Enrollment" : "Selected Plan"}
                                </Badge>
                              )}
                              {(() => {
                                // Show the same date the submit will save (single
                                // source of truth). For OE this is the plan's new
                                // plan-year start, not the employee's current
                                // enrollment date.
                                const badgeDate = resolveEnrollmentEffectiveDate({
                                  enrollmentState,
                                  plan: plan as any,
                                  hasCompletedOpenEnrollment,
                                  oeProgressKey,
                                  qleEventDate,
                                  qleEffectiveDate: qleEffectiveDate ? qleEffectiveDate.toISOString() : null,
                                  newHireEffectiveDate: eligibilityByPlanId.get(plan.id)?.effectiveDate ?? null,
                                });
                                if (!badgeDate) return null;
                                return (
                                  <Badge
                                    variant="secondary"
                                    className="bg-green-100 text-green-800"
                                  >
                                    Effective: {formatDateSafe(badgeDate)}
                                  </Badge>
                                );
                              })()}
                            </div>
                          )}

                          {!isReadOnlyStep && (
                            <div
                              className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <label className="inline-flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer hover:text-foreground select-none">
                                <Checkbox
                                  checked={isInCompare}
                                  onCheckedChange={(checked) =>
                                    handleComparePlan(currentType, plan.id, !!checked)
                                  }
                                  className="h-3.5 w-3.5"
                                />
                                Add to compare
                              </label>
                            </div>
                          )}
                        </div>
                      );
                    });

                  // Hide waive option if any plan in this category is enrollment-required
                  const hasRequiredPlan = (sourcePlans || []).some(
                    (p: any) => String(p.type).toLowerCase() === currentType.toLowerCase() && Boolean(p.enrollmentRequired)
                  );
                  return hasRequiredPlan ? planCards : [...planCards, waiveCard];
                })()}
              </div>
            </div>
              )}

            {/* Reason for waiving (only when the user picked the dashed waive option) */}
            {(() => {
              const currentType = availableBenefitTypes[modifyStep] || "";
              if (!waivedCoverageByType[currentType]) return null;
              if (isLockedStep || isReadOnlyStep) return null;
              return (
                <div className="space-y-2 mt-4">
                  <SectionHeader index={2} title="Reason for waiving" />
                  <WaiveReasonPicker
                    reason={waiverReasonByType[currentType] ?? ""}
                    onReason={(v) =>
                      setWaiverReasonByType((prev) => ({ ...prev, [currentType]: v }))
                    }
                    otherText={waiverDescriptionByType[currentType] ?? ""}
                    onOtherText={(v) =>
                      setWaiverDescriptionByType((prev) => ({ ...prev, [currentType]: v }))
                    }
                  />
                </div>
              );
            })()}

            {/* Dependent Management - Only show if plan selected (not waived) and has dependents */}
            {(() => {
              if (isLockedStep) return null;
              if (isReadOnlyStep) return null;
              const currentType = availableBenefitTypes[modifyStep] || "";
              const isWaived = !!waivedCoverageByType[currentType];
              const hasDependents = dependents && (dependents as any[]).length > 0;
              const hasSelectedPlan = selectedModifyPlans[currentType]?.planId > 0;

              // Hide dependent management for auto-enrollment plans
              const currentSel = selectedModifyPlans[currentType];
              const selPlanObj = currentSel ? (resolvedPlanSource || []).find((sp: any) => sp.id === currentSel.planId) : null;
              if (Boolean((selPlanObj as any)?.enrollmentRequired)) return null;

              // Don't show if waived
              if (isWaived) return null;

              // Compact message if no dependents
              if (!hasDependents) {
                return (
                  <div className="text-sm text-muted-foreground py-2 px-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
                    <span className="font-medium">Dependents:</span> No dependents on file. Coverage will be for employee only.
                  </div>
                );
              }

              // Only show full dependent management if a plan is selected
              if (!hasSelectedPlan) {
                return (
                  <div className="text-sm text-muted-foreground py-2 px-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
                    <span className="font-medium">Dependents:</span> Select a plan above to manage dependent coverage.
                  </div>
                );
              }

              // Gate by the plan's dependent eligibility (Life/AD&D/Disability/FSA cover the employee only).
              const eligibleDeps = eligibleDependentsForPlan(selPlanObj, dependents as any[]);
              if (eligibleDeps.length === 0) {
                return (
                  <div className="text-sm text-muted-foreground py-2 px-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
                    <span className="font-medium">Dependents:</span> This plan covers the employee only — dependents aren't eligible.
                  </div>
                );
              }

              // Mirror ModifyTypeModal's DependentsBody API directly: two parallel
              // maps own truth, no classification or "intent" inference. The dropdown
              // selection lives in depReasonByType; the freeform "Other" text in
              // depOtherTextByType. Both reset together when a dep gets enrolled.
              const currentEnrollmentForDeps = (currentEnrollments as any[])?.find(
                (e: any) =>
                  e.planType === currentType &&
                  !e.waivedCoverage &&
                  String(e.status || "").toLowerCase() !== "waived"
              );
              const currentlyEnrolledDepIds = currentEnrollmentForDeps?.enrolledDependents || [];

              const depDecisions: Record<number, "enroll" | "waive"> = {};
              for (const d of eligibleDeps as any[]) {
                const sel = selectedModifyPlans[currentType];
                const isEnrolled = sel
                  ? sel.dependentIds.includes(d.id)
                  : currentlyEnrolledDepIds.includes(d.id);
                depDecisions[d.id] = isEnrolled ? "enroll" : "waive";
              }
              const depWaiveReasonsMap = depReasonByType[currentType] ?? {};
              const depWaiveOtherTextsMap = depOtherTextByType[currentType] ?? {};

              const setDecision = (id: number, decision: "enroll" | "waive") => {
                setSelectedModifyPlans((prev) => {
                  const base = prev[currentType] ?? { planId: 0, dependentIds: currentlyEnrolledDepIds };
                  const nextIds = decision === "enroll"
                    ? Array.from(new Set([...base.dependentIds, id]))
                    : base.dependentIds.filter((x) => x !== id);
                  return { ...prev, [currentType]: { ...base, dependentIds: nextIds } };
                });
                if (decision === "enroll") {
                  // Clear any prior waive reason + freeform text when enrolling.
                  setDepReasonByType((prev) => {
                    if (!prev[currentType]?.[id]) return prev;
                    const next = { ...prev, [currentType]: { ...prev[currentType] } };
                    delete next[currentType][id];
                    return next;
                  });
                  setDepOtherTextByType((prev) => {
                    if (!prev[currentType]?.[id]) return prev;
                    const next = { ...prev, [currentType]: { ...prev[currentType] } };
                    delete next[currentType][id];
                    return next;
                  });
                }
              };

              const setReason = (id: number, v: string) =>
                setDepReasonByType((prev) => ({
                  ...prev,
                  [currentType]: { ...(prev[currentType] ?? {}), [id]: v },
                }));

              const setOtherText = (id: number, v: string) =>
                setDepOtherTextByType((prev) => ({
                  ...prev,
                  [currentType]: { ...(prev[currentType] ?? {}), [id]: v },
                }));

              return (
                <div className="space-y-2">
                  <SectionHeader index={2} title="Dependents" />
                  <DependentsBody
                    dependents={eligibleDeps as any[]}
                    depDecisions={depDecisions}
                    onSetDecision={setDecision}
                    depWaiveReasons={depWaiveReasonsMap}
                    onSetDepWaiveReason={setReason}
                    depWaiveOtherTexts={depWaiveOtherTextsMap}
                    onSetDepWaiveOtherText={setOtherText}
                  />
                </div>
              );
            })()}

            {/* Beneficiaries (life / AD&D — replaces dependents for these plans) */}
            {(() => {
              if (isLockedStep || isReadOnlyStep) return null;
              const currentType = availableBenefitTypes[modifyStep] || "";
              if (waivedCoverageByType[currentType]) return null;
              const sel = selectedModifyPlans[currentType];
              if (!sel?.planId) return null;
              const selPlan = (resolvedPlanSource || []).find((sp: any) => sp.id === sel.planId);
              if (!requiresBeneficiary(String((selPlan as any)?.type || ""))) return null;
              return (
                <div className="mt-4 space-y-2">
                  <SectionHeader index={2} title="Beneficiaries" />
                  <BeneficiaryAllocation
                    value={sel.beneficiaries ?? defaultBeneficiaryAllocation()}
                    onChange={(beneficiaries) =>
                      setSelectedModifyPlans((prev) => ({
                        ...prev,
                        [currentType]: { ...prev[currentType], beneficiaries },
                      }))
                    }
                  />
                </div>
              );
            })()}

            {/* Cost Summary: running total across all benefit types, current step highlighted */}
            {(() => {
              const currentType = availableBenefitTypes[modifyStep] || "";
              const stepLabels: Record<string, string> = {
                health: "Medical",
                dental: "Dental",
                vision: "Vision",
                life: "Life",
                disability: "Disability",
              };
              const coverageTierLabels: Record<string, string> = {
                employee_only: "Employee Only",
                employee_spouse: "Employee + Spouse",
                employee_children: "Employee + Children",
                family: "Employee + Family",
              };

              // Running total calculations (across all benefit types)
              // Employer/total split covers only the NEW selections, not current enrollments.
              const costsByType: Record<string, {
                current: number;
                new: number;
                newEmployer: number;
                newTotal: number;
                coverageLabel: string | null;
                fsaAnnualElection: number | null;
                fsaPerPaycheck: number | null;
              }> = {};

              availableBenefitTypes.forEach((benefitType) => {
                // Current cost for this benefit type (from active enrollments)
                const currentForType = (currentEnrollments as any[])
                  ?.filter((e: any) =>
                    isActiveEnrollment(e) &&
                    String(e.planType || "").toLowerCase() === benefitType
                  )
                  ?.reduce((sum: number, e: any) => sum + (Number(e.monthlyPremium) || 0), 0) || 0;

                // New cost for this benefit type (from selections)
                let newForType = 0;
                let newEmployerForType = 0;
                let newTotalForType = 0;
                let coverageLabel: string | null = null;
                let fsaAnnualElection: number | null = null;
                let fsaPerPaycheck: number | null = null;
                const selection = selectedModifyPlans[benefitType];
                if (selection && selection.planId > 0) {
                  const plan = (employeeAllPlans as any[])?.find(
                    (p: any) => p.id === selection.planId
                  ) || (employeePlansData as any[])?.find(
                    (p: any) => p.id === selection.planId
                  );

                  if (plan && isFsaPlanType(String(plan.type || ""))) {
                    // FSA contributes its election (monthly-equivalent), not a rate.
                    const fsaCost = fsaCostFor(plan, selection.annualElection);
                    newForType = fsaCost.monthlyEquivalent;
                    newTotalForType = newForType;
                    fsaAnnualElection = Number(selection.annualElection) || 0;
                    // Prorated over the paychecks left in the plan year, so it isn't
                    // monthlyEquivalent converted — shown as-is in the FSA row.
                    fsaPerPaycheck = fsaCost.perPaycheck;
                  } else if (plan) {
                    const typeResult = computePremiumResultForSelection({
                      plan,
                      ratesByPlanId: selectedPlanRates as any,
                      contribsByPlanId: selectedPlanContribs as any,
                      employee: employeeRow as any,
                      selectedDependentIds: selection.dependentIds,
                      allDependents: dependents as any[],
                      ldConfigByPlanId,
                    });
                    newForType = typeResult?.employeePremium ?? 0;
                    newEmployerForType = typeResult?.employerPremium ?? 0;
                    newTotalForType = typeResult?.totalMonthlyRate ?? newForType;
                    coverageLabel =
                      coverageTierLabels[determineCoverageTierFromDependents(dependents as any[], selection.dependentIds)] ||
                      "Employee Only";
                  }
                }

                costsByType[benefitType] = {
                  current: currentForType,
                  new: newForType,
                  newEmployer: newEmployerForType,
                  newTotal: newTotalForType,
                  coverageLabel,
                  fsaAnnualElection,
                  fsaPerPaycheck,
                };
              });

              const currentMonthlyCost = Object.values(costsByType).reduce((sum, c) => sum + c.current, 0);
              const newMonthlyCost = Object.values(costsByType).reduce((sum, c) => sum + c.new, 0);
              const newEmployerMonthlyCost = Object.values(costsByType).reduce((sum, c) => sum + c.newEmployer, 0);
              const newTotalMonthlyCost = Object.values(costsByType).reduce((sum, c) => sum + c.newTotal, 0);
              const isNonMonthlyCycle = hasNonMonthlyPayCycle(employeeRow?.payCycle);
              const toCycle = (monthly: number) =>
                isNonMonthlyCycle ? calculatePerPaycheckAmount(monthly, employeeRow?.payCycle) : monthly;
              const cycleCurrency = (monthly: number) => formatCurrency(toCycle(monthly));
              const signedCurrency = (amount: number) => `${amount > 0 ? "+" : ""}${formatCurrency(amount)}`;
              const periodWord = isNonMonthlyCycle ? "paycheck" : "month";
              const monthlyDifference = newMonthlyCost - currentMonthlyCost;

              const annualDifference = monthlyDifference * 12;
              // 700 shades: 600s fail text contrast on the purple-50 card.
              const changeTone = (amount: number) =>
                amount > 0 ? "text-red-700 dark:text-red-400" : amount < 0 ? "text-green-700 dark:text-green-400" : "text-gray-600 dark:text-gray-400";
              const showWhoPays = allowBreakdown && newTotalMonthlyCost > 0;
              const employerSharePercent = showWhoPays ? (newEmployerMonthlyCost / newTotalMonthlyCost) * 100 : 0;
              const tallyRowTypes = availableBenefitTypes.filter(
                (benefitType) => (selectedModifyPlans[benefitType]?.planId ?? 0) > 0 || !!waivedCoverageByType[benefitType],
              );

              return (
                <div className="space-y-4">
                  <SectionHeader index={3} title="Cost summary" />
                  <h4 className="font-medium sr-only">Cost Summary</h4>

                  {/* Running Cost Tally — employer split sections/columns only when allowBreakdown */}
                  <div className="bg-purple-50 dark:bg-purple-950/20 rounded-lg p-4 space-y-5">
                    <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                      <h4 className="font-semibold text-purple-900 dark:text-purple-100">Running Cost Tally</h4>
                      <span className="text-xs text-purple-800 dark:text-purple-300">
                        {isNonMonthlyCycle
                          ? `${getPayCycleLabelCapitalized(employeeRow?.payCycle)} deductions · monthly in brackets`
                          : "Monthly deductions"}
                      </span>
                    </div>

                    {/* Your cost */}
                    <div className="space-y-2">
                      <div className="text-[11px] font-semibold uppercase tracking-wider text-purple-800 dark:text-purple-300">
                        Your cost
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-[minmax(0,1fr)_2rem_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)] md:items-end gap-4 text-sm">
                        <div>
                          <div className="text-purple-800 dark:text-purple-300">Current</div>
                          <p className="text-lg font-medium text-gray-600 dark:text-gray-300">{cycleCurrency(currentMonthlyCost)}</p>
                        </div>
                        <div className="hidden md:block text-lg text-center text-purple-800 dark:text-purple-300" aria-hidden="true">→</div>
                        <div>
                          <div className="text-purple-800 dark:text-purple-300">New</div>
                          <p className="text-xl font-bold">{cycleCurrency(newMonthlyCost)}</p>
                        </div>
                        <div>
                          <div className="text-purple-800 dark:text-purple-300">Difference</div>
                          <p className={`text-lg font-semibold ${changeTone(monthlyDifference)}`}>
                            {signedCurrency(toCycle(monthlyDifference))}
                          </p>
                          <p className={`text-xs ${changeTone(monthlyDifference)}`}>
                            {monthlyDifference > 0 ? `more per ${periodWord}` : monthlyDifference < 0 ? `less per ${periodWord}` : "no change"}
                          </p>
                        </div>
                        <div>
                          <div className="text-purple-800 dark:text-purple-300">Annual impact</div>
                          <p className={`text-lg font-semibold ${changeTone(annualDifference)}`}>
                            {signedCurrency(annualDifference)}
                          </p>
                          <p className={`text-xs ${changeTone(annualDifference)}`}>
                            {annualDifference > 0 ? "more per year" : annualDifference < 0 ? "saved per year" : "no change"}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* New plans: who pays */}
                    {showWhoPays && (
                      <div className="space-y-2 pt-4 border-t border-purple-200 dark:border-purple-800">
                        <div className="text-[11px] font-semibold uppercase tracking-wider text-purple-800 dark:text-purple-300">
                          New plans: who pays
                        </div>
                        <div className="flex h-3 rounded-full overflow-hidden bg-purple-100 dark:bg-purple-900/40" aria-hidden="true">
                          <div className="bg-purple-300 dark:bg-purple-400" style={{ width: `${employerSharePercent}%` }} />
                          <div className="bg-purple-700 dark:bg-purple-200 flex-1" />
                        </div>
                        <div className="grid grid-cols-[minmax(0,1fr)_1.5rem_minmax(0,1fr)_1.5rem_minmax(0,1fr)] items-start gap-2 text-sm">
                          <div>
                            <div className="text-purple-800 dark:text-purple-300">Total premium</div>
                            <p className="text-lg font-semibold">{cycleCurrency(newTotalMonthlyCost)}</p>
                            {isNonMonthlyCycle && (
                              <p className="text-xs text-muted-foreground">({formatCurrency(newTotalMonthlyCost)}/mo)</p>
                            )}
                          </div>
                          <div className="text-lg text-center text-purple-800 dark:text-purple-300 pt-4" aria-hidden="true">=</div>
                          <div>
                            <div className="flex items-center gap-1.5 text-purple-800 dark:text-purple-300">
                              <span className="h-2.5 w-2.5 rounded-sm bg-purple-300 dark:bg-purple-400" aria-hidden="true" />
                              Employer
                            </div>
                            <p className="text-lg font-semibold">{cycleCurrency(newEmployerMonthlyCost)}</p>
                            {isNonMonthlyCycle && (
                              <p className="text-xs text-muted-foreground">({formatCurrency(newEmployerMonthlyCost)}/mo)</p>
                            )}
                          </div>
                          <div className="text-lg text-center text-purple-800 dark:text-purple-300 pt-4" aria-hidden="true">+</div>
                          <div>
                            <div className="flex items-center gap-1.5 text-purple-800 dark:text-purple-300">
                              <span className="h-2.5 w-2.5 rounded-sm bg-purple-700 dark:bg-purple-200" aria-hidden="true" />
                              You
                            </div>
                            <p className="text-lg font-semibold">{cycleCurrency(newMonthlyCost)}</p>
                            {isNonMonthlyCycle && (
                              <p className="text-xs text-muted-foreground">({formatCurrency(newMonthlyCost)}/mo)</p>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* By plan type */}
                    <div className="space-y-2 pt-4 border-t border-purple-200 dark:border-purple-800">
                      <div className="text-[11px] font-semibold uppercase tracking-wider text-purple-800 dark:text-purple-300">
                        By plan type
                      </div>
                      {tallyRowTypes.length === 0 ? (
                        <div className="text-xs text-purple-700 dark:text-purple-400 italic">No plans selected yet</div>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="text-xs text-purple-800 dark:text-purple-300 text-right">
                                <th className="text-left font-medium py-1 px-2">Type</th>
                                <th className="font-medium py-1">Current</th>
                                <th className="font-medium py-1">New (you)</th>
                                <th className={`font-medium py-1 ${allowBreakdown ? "" : "pr-2"}`}>Change</th>
                                {allowBreakdown && <th className="font-medium py-1">Employer</th>}
                                {allowBreakdown && <th className="font-medium py-1 pr-2">Total premium</th>}
                              </tr>
                            </thead>
                            <tbody>
                              {tallyRowTypes.map((benefitType) => {
                                const costs = costsByType[benefitType];
                                const isWaivedType = !!waivedCoverageByType[benefitType];
                                const isCurrentStep = benefitType === currentType;
                                // FSA: real prorated per-paycheck deduction, not the monthly equivalent converted.
                                const newCycleAmount =
                                  isNonMonthlyCycle && costs.fsaPerPaycheck != null ? costs.fsaPerPaycheck : toCycle(costs.new);
                                const totalCycleAmount =
                                  isNonMonthlyCycle && costs.fsaPerPaycheck != null ? costs.fsaPerPaycheck : toCycle(costs.newTotal);
                                const rowDiff = newCycleAmount - toCycle(costs.current);
                                const rowDetail = isWaivedType
                                  ? null
                                  : costs.fsaAnnualElection != null
                                    ? `${formatCurrency(costs.fsaAnnualElection)}/yr election`
                                    : costs.coverageLabel;
                                return (
                                  <tr
                                    key={benefitType}
                                    className={`text-right ${isCurrentStep ? "bg-purple-100 dark:bg-purple-900/40" : "border-t border-purple-100 dark:border-purple-900"}`}
                                    aria-current={isCurrentStep ? "step" : undefined}
                                  >
                                    <td className="text-left py-2 px-2 rounded-l-md">
                                      <div className="flex items-center gap-2">
                                        <span className={isCurrentStep ? "font-semibold" : "font-medium"}>
                                          {stepLabels[benefitType] || getPlanTypeMeta(benefitType).label}
                                        </span>
                                        {isCurrentStep && (
                                          <span className="rounded bg-purple-700 px-1.5 py-px text-[11px] font-semibold text-white dark:bg-purple-300 dark:text-purple-950">
                                            This step
                                          </span>
                                        )}
                                      </div>
                                      {rowDetail && (
                                        <div className="text-xs text-purple-800 dark:text-purple-300">{rowDetail}</div>
                                      )}
                                    </td>
                                    <td className="py-2 text-gray-600 dark:text-gray-300">{cycleCurrency(costs.current)}</td>
                                    <td className="py-2 font-semibold">
                                      {isWaivedType ? (
                                        <span className="font-medium text-orange-700 dark:text-orange-400">Waived</span>
                                      ) : (
                                        formatCurrency(newCycleAmount)
                                      )}
                                    </td>
                                    <td className={`py-2 ${allowBreakdown ? "" : "pr-2 rounded-r-md"} ${changeTone(rowDiff)}`}>{signedCurrency(rowDiff)}</td>
                                    {allowBreakdown && (
                                      <td className="py-2">{isWaivedType ? "—" : cycleCurrency(costs.newEmployer)}</td>
                                    )}
                                    {allowBreakdown && (
                                      <td className="py-2 pr-2 rounded-r-md">{isWaivedType ? "—" : formatCurrency(totalCycleAmount)}</td>
                                    )}
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Action Buttons */}
            <div className="flex justify-between gap-3 pt-4 border-t">
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowModifyBenefitsModal(false)}
                >
                  Cancel
                </Button>
                {modifyStep > 0 && (
                  <Button
                    variant="outline"
                    onClick={() => setModifyStep((prev) => prev - 1)}
                  >
                    Previous
                  </Button>
                )}
              </div>
              <div className="flex gap-3">
                {modifyStep < availableBenefitTypes.length - 1 ? (
                  <Button
                    className="bg-blue-600 hover:bg-blue-700"
                    onClick={() => {
                      const currentType = availableBenefitTypes[modifyStep] || "";
                      // Elect-or-waive is mandatory: a step can't be skipped
                      // without a decision (locked steps carry one already).
                      if (!isLockedStep && !isReadOnlyStep && !validateDecisionMade(currentType)) return;
                      if (!validateWaiverReasons(currentType)) return;
                      if (!validateFsaElection(currentType)) return;
                      setModifyStep((prev) => prev + 1);
                    }}
                  >
                    Save & Continue
                  </Button>
                ) : (
                  <Button
                    className={availableBenefitTypes.some((t) => hasActualChangeForType(t))
                      ? "bg-green-600 hover:bg-green-700"
                      : "bg-blue-600 hover:bg-blue-700"}
                    onClick={() => {
                      const currentType = availableBenefitTypes[modifyStep] || "";
                      if (!validateWaiverReasons(currentType)) return;
                      // Elect-or-waive is mandatory for EVERY benefit type —
                      // jump to the first one still missing a decision.
                      for (const type of availableBenefitTypes) {
                        if (!hasDecisionForType(type)) {
                          setModifyStep(availableBenefitTypes.indexOf(type));
                          validateDecisionMade(type);
                          return;
                        }
                      }
                      // Validate EVERY selected FSA plan before leaving the modal for
                      // review/signature. Jump to the offending step so the user sees it.
                      for (const type of availableBenefitTypes) {
                        if (!validateFsaElection(type)) {
                          setModifyStep(availableBenefitTypes.indexOf(type));
                          return;
                        }
                      }
                      setShowSummaryPage(true);
                    }}
                  >
                    {availableBenefitTypes.some((t) => hasActualChangeForType(t))
                      ? "Complete Enrollment"
                      : "Review Elections"}
                  </Button>
                )}
              </div>
            </div>
          </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Plan Comparison Dialog — launched from the Modify Benefits modal. */}
      <PlanComparisonDialog
        open={compareDialogType !== null}
        onOpenChange={(open) => { if (!open) setCompareDialogType(null); }}
        plans={(() => {
          if (!compareDialogType) return [];
          const ids = comparePlans[compareDialogType] || [];
          const source = (resolvedPlanSource as any[]) || [];
          return ids
            .map((id) => source.find((p: any) => p.id === id))
            .filter(Boolean)
            .map((p: any): ComparablePlan => ({
              id: p.id,
              name: p.name,
              type: p.type,
              networkType: p.networkType,
              metalTier: p.metalTier,
              description: p.description,
              carrierName: p.carrier?.name || p.carrierName || null,
            }));
        })()}
        getPremium={(planId) => {
          if (!compareDialogType) return null;
          const source = (resolvedPlanSource as any[]) || [];
          const plan = source.find((p: any) => p.id === planId);
          if (!plan) return null;
          const selectedDeps =
            selectedModifyPlans[compareDialogType]?.dependentIds || [];
          try {
            return calculatePremiumForSelection({
              plan,
              ratesByPlanId: ratesByPlanId as any,
              contribsByPlanId: contribsByPlanId as any,
              employee: employeeRow as any,
              selectedDependentIds: selectedDeps,
              allDependents: dependents as any[],
              ldConfigByPlanId,
            });
          } catch {
            return null;
          }
        }}
        onRemovePlan={(planId) => {
          if (!compareDialogType) return;
          handleComparePlan(compareDialogType, planId, false);
        }}
      />

      {/* Pending Modifications Modal */}
      <Dialog
        open={showPendingModificationsModal}
        onOpenChange={setShowPendingModificationsModal}
      >
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
              Pending Benefit Modifications
            </DialogTitle>
            <DialogDescription>
              Review your submitted benefit changes that are awaiting approval
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {pendingEnrollments.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500">No pending modifications found</p>
              </div>
            ) : (
              pendingEnrollments.map((enrollment) => (
                <Card
                  key={enrollment.id}
                  className="border-yellow-200 bg-yellow-50 dark:bg-yellow-950/20"
                >
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-lg text-yellow-800 dark:text-yellow-200">
                          {enrollment.planName}
                        </CardTitle>
                        <CardDescription className="text-yellow-700 dark:text-yellow-300">
                          Submitted:{" "}
                          {formatDateDisplay(enrollment.submittedDate)}{" "}
                          at{" "}
                          {(parseDateLocal(enrollment.submittedDate) ?? new Date()).toLocaleTimeString()}
                        </CardDescription>
                      </div>
                      <Badge
                        variant="outline"
                        className="border-yellow-400 text-yellow-700 dark:text-yellow-300"
                      >
                        Pending Review
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Plan Details */}
                      <div>
                        <h4 className="font-medium text-yellow-800 dark:text-yellow-200 mb-2">
                          Plan Details
                        </h4>
                        <div className="space-y-1 text-sm">
                          <p>
                            <span className="text-yellow-700 dark:text-yellow-300">
                              Type:
                            </span>{" "}
                            {enrollment.planType}
                          </p>
                          <p>
                            <span className="text-yellow-700 dark:text-yellow-300">
                              Monthly Premium:
                            </span>{" "}
                            ${enrollment.monthlyPremium}
                          </p>
                        </div>
                      </div>

                      {/* Enrolled Dependents */}
                      <div>
                        <h4 className="font-medium text-yellow-800 dark:text-yellow-200 mb-2">
                          Enrolled Dependents
                        </h4>
                        {enrollment.enrolledDependents &&
                        enrollment.enrolledDependents.length > 0 ? (
                          <div className="space-y-1 text-sm">
                            {enrollment.enrolledDependents.map(
                              (depId: number) => {
                                const dependent = Array.isArray(dependents)
                                  ? dependents.find((d: any) => d.id === depId)
                                  : null;
                                return dependent ? (
                                  <div
                                    key={depId}
                                    className="flex items-center gap-2"
                                  >
                                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                    <span>
                                      {dependent.firstName} {dependent.lastName}{" "}
                                      ({dependent.relationship})
                                    </span>
                                  </div>
                                ) : null;
                              }
                            )}
                          </div>
                        ) : (
                          <p className="text-sm text-yellow-600 dark:text-yellow-400 italic">
                            Employee only
                          </p>
                        )}
                      </div>

                      {/* Signature Info */}
                      <div>
                        <h4 className="font-medium text-yellow-800 dark:text-yellow-200 mb-2">
                          Signature
                        </h4>
                        <div className="space-y-1 text-sm">
                          <p>
                            <span className="text-yellow-700 dark:text-yellow-300">
                              Signed by:
                            </span>{" "}
                            {enrollment.signature?.name}
                          </p>
                          <p>
                            <span className="text-yellow-700 dark:text-yellow-300">
                              Date:
                            </span>{" "}
                            {formatDateDisplay(enrollment.signature?.timestamp)}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Waive Reasons if any */}
                    {enrollment.waiveReasons &&
                      Object.keys(enrollment.waiveReasons).length > 0 && (
                        <div className="mt-4 pt-4 border-t border-yellow-200">
                          <h4 className="font-medium text-yellow-800 dark:text-yellow-200 mb-2">
                            Waived Dependents
                          </h4>
                          <div className="space-y-2">
                            {Object.entries(enrollment.waiveReasons).map(
                              ([depId, reason]: [string, any]) => {
                                const dependent = Array.isArray(dependents)
                                  ? dependents.find(
                                      (d: any) => d.id === parseInt(depId)
                                    )
                                  : null;
                                return dependent ? (
                                  <div key={depId} className="text-sm">
                                    <div className="flex items-center gap-2 mb-1">
                                      <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                                      <span className="font-medium">
                                        {dependent.firstName}{" "}
                                        {dependent.lastName}
                                      </span>
                                    </div>
                                    <p className="ml-4 text-yellow-600 dark:text-yellow-400 italic">
                                      Reason: {reason}
                                    </p>
                                  </div>
                                ) : null;
                              }
                            )}
                          </div>
                        </div>
                      )}
                  </CardContent>
                </Card>
              ))
            )}

            {/* Action Buttons */}
            <div className="flex justify-between gap-3 pt-4 border-t">
              <Button
                variant="outline"
                className="bg-blue-600 hover:bg-blue-700 text-white border-blue-600"
                onClick={() => {
                  // Pre-populate modify benefits modal with pending enrollment data
                  const pendingPlans: {
                    [key: string]: { planId: number; dependentIds: number[] };
                  } = {};
                  const pendingWaiveReasons: {
                    [key: string]: { [dependentId: string]: string };
                  } = {};

                  pendingEnrollments.forEach((enrollment) => {
                    pendingPlans[enrollment.planType] = {
                      planId: enrollment.planId,
                      dependentIds: enrollment.enrolledDependents || [],
                    };

                    if (enrollment.waiveReasons) {
                      pendingWaiveReasons[enrollment.planType] =
                        enrollment.waiveReasons;
                    }
                  });

                  setSelectedModifyPlans(pendingPlans);
                  // Classify pending enrollment dep-waive strings into the two-map shape.
                  const pendingDepReasons: Record<string, Record<number, string>> = {};
                  const pendingDepOtherTexts: Record<string, Record<number, string>> = {};
                  for (const [type, perDep] of Object.entries(pendingWaiveReasons)) {
                    pendingDepReasons[type] = {};
                    pendingDepOtherTexts[type] = {};
                    for (const [depId, stored] of Object.entries(perDep)) {
                      const cls = classifyDepReason(stored as string);
                      if (cls.reason) pendingDepReasons[type][Number(depId)] = cls.reason;
                      if (cls.otherText) pendingDepOtherTexts[type][Number(depId)] = cls.otherText;
                    }
                  }
                  setDepReasonByType(pendingDepReasons);
                  setDepOtherTextByType(pendingDepOtherTexts);
                  setShowPendingModificationsModal(false);
                  setShowModifyBenefitsModal(true);
                  setModifyStep(0);
                }}
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit Pending Modifications
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowPendingModificationsModal(false)}
              >
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Beneficiaries Manager Component
function BeneficiariesManager({
  planId,
  beneficiaries,
  onClose,
  onUpdate,
}: {
  planId: number | null;
  beneficiaries: any[];
  onClose: () => void;
  onUpdate: () => void;
}) {
  const [editingBeneficiaries, setEditingBeneficiaries] = useState(
    beneficiaries || []
  );
  const [newBeneficiary, setNewBeneficiary] = useState({
    firstName: "",
    lastName: "",
    relationship: "",
    dateOfBirth: "",
    percentage: 0,
    ssn: "",
    address: "",
    phone: "",
  });
  const [isAdding, setIsAdding] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const handleNewBeneficiaryPhoneChange = (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const formattedPhone = formatPhoneInputValue(event.target.value);

    setNewBeneficiary(previous => ({
      ...previous,
      phone: formattedPhone,
    }));
  };

  useEffect(() => {
    setEditingBeneficiaries(beneficiaries || []);
  }, [beneficiaries]);

  const totalPercentage = editingBeneficiaries.reduce(
    (sum: number, ben: any) => sum + (ben.percentage || 0),
    0
  );

  const updateBeneficiaryMutation = useMutation({
    mutationFn: async (updatedBeneficiaries: any[]) => {
      const response = await apiRequest(
        "PUT",
        `/api/employees/benefits/beneficiaries/${planId}`,
        { beneficiaries: updatedBeneficiaries }
      );
      return await response.json();
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Beneficiaries updated successfully.",
      });
      onUpdate();
      onClose();
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update beneficiaries.",
        variant: "destructive",
      });
    },
  });

  const handlePercentageChange = (index: number, newPercentage: number) => {
    const updated = [...editingBeneficiaries];
    updated[index].percentage = Math.max(0, Math.min(100, newPercentage));
    setEditingBeneficiaries(updated);
  };

  const removeBeneficiary = (index: number) => {
    const updated = editingBeneficiaries.filter((_, i) => i !== index);
    setEditingBeneficiaries(updated);
  };

  const addBeneficiary = () => {
    if (
      !newBeneficiary.firstName ||
      !newBeneficiary.lastName ||
      !newBeneficiary.relationship
    ) {
      toast({
        title: "Error",
        description: "Please fill in all required fields.",
        variant: "destructive",
      });
      return;
    }

    const updated = [
      ...editingBeneficiaries,
      { ...newBeneficiary, id: Date.now() },
    ];
    setEditingBeneficiaries(updated);
    setNewBeneficiary({
      firstName: "",
      lastName: "",
      relationship: "",
      dateOfBirth: "",
      percentage: 0,
      ssn: "",
      address: "",
      phone: "",
    });
    setIsAdding(false);
  };

  const handleSave = () => {
    if (totalPercentage !== 100) {
      toast({
        title: "Error",
        description: "Total allocation must equal 100%.",
        variant: "destructive",
      });
      return;
    }

    updateBeneficiaryMutation.mutate(editingBeneficiaries);
  };

  return (
    <div className="space-y-6">
      {/* Current Beneficiaries */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Current Beneficiaries</h3>
          <div
            className={`text-sm font-medium ${
              totalPercentage === 100 ? "text-green-600" : "text-red-600"
            }`}
          >
            Total: {totalPercentage}%
          </div>
        </div>

        {/* DEV-177: misplaced OE plan list + EnrollmentWizard inside the
            beneficiaries popup; duplicates the main /benefits OE flow.
            Disabled (kept for reference) pending removal. */}
        {false && (<>
        {/* Open Enrollment Plans (Employee self-service) */}
        <Card className="mt-4">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Open Enrollment Plans
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Select a plan below to start your open enrollment. You can review
              dependents and confirm before submitting.
            </p>
          </CardHeader>
          <CardContent>
            {!oeWindow ? (
              <div className="text-sm text-muted-foreground">
                No open enrollment window is configured for your company yet.
              </div>
            ) : eligibleOePlans.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p className="font-medium mb-2">No Plans Available</p>
                <p className="text-sm">
                  No benefit plans have been configured for this open enrollment period.
                  Please contact your HR administrator for more information.
                </p>
              </div>
            ) : (
              <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                {eligibleOePlans.map((p: any) => (
                  <div
                    key={p.id}
                    className="border rounded-lg p-3 flex flex-col gap-2"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-sm">{p.name}</div>
                        <div className="text-xs text-muted-foreground capitalize">
                          {p.type}
                        </div>
                      </div>
                    </div>
                    <div className="flex-1 text-xs text-muted-foreground line-clamp-2">
                      {p.description || "Plan details available in SBC"}
                    </div>
                    <div className="pt-1">
                      {isLifeDisabilityType(String(p.type || "")) && !ldConfigByPlanId[p.id] ? (
                        <div className="text-xs text-amber-600 dark:text-amber-400 text-center py-1.5">
                          Configuration pending — enrollment not yet available
                        </div>
                      ) : (
                        <Button
                          size="sm"
                          className="w-full"
                          onClick={() => setWizardPlan(p)}
                        >
                          Enroll in this plan
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Employee Confirmation for Open Enrollment */}
        <OpenEnrollmentConfirmation
          isActive={!!oeWindow}
          status={oeProgressRow?.status}
          isLoading={confirmOeSelection.isLoading}
          onReview={() => setWizardPlan(null)}
          onConfirm={() => confirmOeSelection.mutate()}
        />

        {wizardPlan && employeeRow && (
          <div className="mt-4">
            <EnrollmentWizard
              plan={wizardPlan as any}
              employee={employeeRow as any}
              dependents={
                (Array.isArray(dependents) ? (dependents as any[]) : []) as any
              }
              onBackToSelection={() => setWizardPlan(null)}
              onEnrollmentComplete={(data: any) => {
                const payload = {
                  employeeId: employeeRow.id,
                  planId: data.planId,
                  clientId: user?.clientId,
                  coverageTier: data.coverageTier,
                  dependentIds: Array.isArray(data.dependents)
                    ? data.dependents
                    : [],
                  beneficiaryName: data.beneficiaryName || null,
                  beneficiaryRelationship: data.beneficiaryRelationship || null,
                  waivedCoverage: !!data.waivedCoverage,
                  waiverReason: data.waiverReason || null,
                  enrollmentSource: "open_enrollment",
                  effectiveDate: enrollmentState === "qualifying_event" && qleEventDate
                    ? clampEffectiveToPlanStart(calculateQLEEffectiveDate(qleEventDate, "first_of_month"), wizardPlan).toISOString()
                    : (wizardPlan as any)?.renewalDate
                      ? new Date((wizardPlan as any).renewalDate).toISOString()
                      : new Date().toISOString(),
                } as any;
                submitOeEnrollment.mutate(payload);
              }}
            />
          </div>
        )}
        </>)}

        {editingBeneficiaries.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No beneficiaries added yet. Click "Add Beneficiary" to get started.
          </div>
        ) : (
          <div className="space-y-3">
            {editingBeneficiaries.map((beneficiary: any, index: number) => (
              <Card key={beneficiary.id || index} className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-sm font-medium">Name</Label>
                    <div className="text-sm">
                      {beneficiary.firstName} {beneficiary.lastName}
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Relationship</Label>
                    <div className="text-sm capitalize">
                      {beneficiary.relationship}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <Label className="text-sm font-medium">Percentage</Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={beneficiary.percentage || 0}
                        onChange={(e) =>
                          handlePercentageChange(
                            index,
                            parseInt(e.target.value) || 0
                          )
                        }
                        className="w-20"
                      />
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => removeBeneficiary(index)}
                      className="mt-5 text-red-600 hover:text-red-700"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Add New Beneficiary */}
      <div className="border-t pt-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Add New Beneficiary</h3>
          <Button variant="outline" onClick={() => setIsAdding(!isAdding)}>
            {isAdding ? "Cancel" : "Add Beneficiary"}
          </Button>
        </div>

        {isAdding && (
          <Card className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="firstName">First Name *</Label>
                <Input
                  id="firstName"
                  value={newBeneficiary.firstName}
                  onChange={(e) =>
                    setNewBeneficiary({
                      ...newBeneficiary,
                      firstName: e.target.value,
                    })
                  }
                />
              </div>
              <div>
                <Label htmlFor="lastName">Last Name *</Label>
                <Input
                  id="lastName"
                  value={newBeneficiary.lastName}
                  onChange={(e) =>
                    setNewBeneficiary({
                      ...newBeneficiary,
                      lastName: e.target.value,
                    })
                  }
                />
              </div>
              <div>
                <Label htmlFor="relationship">Relationship *</Label>
                <Select
                  value={newBeneficiary.relationship}
                  onValueChange={(value) =>
                    setNewBeneficiary({
                      ...newBeneficiary,
                      relationship: value,
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select relationship" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="spouse">Spouse</SelectItem>
                    <SelectItem value="child">Child</SelectItem>
                    <SelectItem value="parent">Parent</SelectItem>
                    <SelectItem value="sibling">Sibling</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="dateOfBirth">Date of Birth</Label>
                <Input
                  id="dateOfBirth"
                  type="date"
                  value={newBeneficiary.dateOfBirth}
                  onChange={(e) =>
                    setNewBeneficiary({
                      ...newBeneficiary,
                      dateOfBirth: e.target.value,
                    })
                  }
                />
              </div>
              <div>
                <Label htmlFor="percentage">Percentage *</Label>
                <Input
                  id="percentage"
                  type="number"
                  min="0"
                  max="100"
                  value={newBeneficiary.percentage}
                  onChange={(e) =>
                    setNewBeneficiary({
                      ...newBeneficiary,
                      percentage: parseInt(e.target.value) || 0,
                    })
                  }
                />
              </div>
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={newBeneficiary.phone}
                  onChange={handleNewBeneficiaryPhoneChange}
                />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="address">Address</Label>
                <Textarea
                  id="address"
                  value={newBeneficiary.address}
                  onChange={(e) =>
                    setNewBeneficiary({
                      ...newBeneficiary,
                      address: e.target.value,
                    })
                  }
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setIsAdding(false)}>
                Cancel
              </Button>
              <Button onClick={addBeneficiary}>Add Beneficiary</Button>
            </div>
          </Card>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-2 pt-4 border-t">
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={
            totalPercentage !== 100 || updateBeneficiaryMutation.isPending
          }
          className="bg-green-600 hover:bg-green-700"
        >
          {updateBeneficiaryMutation.isPending ? "Saving..." : "Save Changes"}
        </Button>
      </div>
    </div>
  );
}
